import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import dayjs from 'dayjs';
import ConfirmationModal from "../components/ConfirmationModal";
import TaskSectionTabs, { TASK_TAB_MANAGEMENT } from "../components/TaskSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import DuplicateSuccessMessage from "../components/DuplicateSuccessMessage";
import TaskAddModal from "../components/TaskAddModal";
import SmartAddModal from "../components/SmartAddModal";
import ImportTasks from "./ImportTasks";
import SearchableSelect from "../components/SearchableSelect";
import CollapsibleProjectHeader from "../components/CollapsibleProjectHeader";
import ColumnCustomizerModal from "../components/ColumnCustomizerModal";
import BulkUpdateTasksModal from "../components/BulkUpdateTasksModal";
import ListFiltersToolbarButton from "../components/entity/ListFiltersToolbarButton";
import EntityLabelCell from "../components/entity/EntityLabelCell";
import EntityLabelFilterBar from "../components/entity/EntityLabelFilterBar";
import HorizontalScrollHint from "../components/HorizontalScrollHint";
import EntityLabelsModal from "../components/entity/EntityLabelsModal";
import TaskListFiltersSlideOver from "../components/TaskListFiltersSlideOver";
import { TableActionColumnHeader } from "../components/TableShell";
import { useAdvancedFilter } from "../shared/hooks/useAdvancedFilter";
import { useEntityLabelFilter } from "../shared/hooks/useEntityLabelFilter";
import {
  filterTasksByAdvancedCriteria,
  normalizeTaskPriorityForFilter,
} from "../shared/utils/taskAdvancedFilter";
import DetailSaveStatus from "../components/DetailSaveStatus";
import LinkifiedEditableRead from "../components/LinkifiedEditableRead";
import DateTimeFieldGroup from "../components/DateTimeFieldGroup";
import ViewModeIconToggle from "../components/ViewModeIconToggle";
import ListPaginationBar from "../components/ListPaginationBar";
import { useListPagination } from "../shared/hooks/useListPagination";
import { usePageViewPreferences } from "../shared/hooks/usePageViewPreferences";
import ScheduleCalendar from "../components/ScheduleCalendar";
import { useAuth } from '../contexts/AuthContext';
import { useEffectiveWorkingHours } from '../shared/hooks/useAccountWorkingHours';
import { IconEdit, IconDelete, IconDuplicate, IconReopen, IconClose, IconSave, IconCancel, IconDocPlus, IconLightning, IconCalendar, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import { buildTimeOptionsForWorkingStart, getDueDatePart, getDueTimePart, getDueDateForInput, getDueDateForApi, formatDueDate, formatDueDateTimeForTable, formatScheduledSlot, getDueInstantForComparison } from '../shared/utils/dueDateTime';
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";
import { getTaskScheduleStatusLabel, getTaskScheduleStatusSortRank, getScheduleStatusBadgeStyle, scheduleStatusParamToLabel, taskHasTimeBlocks } from '../shared/utils/taskScheduleStatus';
import { validateDate } from '../shared/utils/validation';
import useBlurAutoSave from "../shared/hooks/useBlurAutoSave";
import { fieldCommitOnEnterKeyDown } from "../shared/utils/fieldCommitOnEnterKeyDown";
import { getAssetLabel } from "../shared/utils/assetLabel";
import { buildDuplicateLabel } from "../shared/utils/duplicateLabel";
import { formatTimestampForTable, formatTimestampDateForTable } from "../shared/utils/dateDisplay";
import { formatChildCascadeLines } from "../shared/utils/cascadeSummary";
import { bulkApplyLabelsToItems, getSharedLabelsAmongItems } from "../shared/utils/entityBulkLabels";
import {
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import {
  entityLabelsEqual,
  getItemLabels,
  normalizeEntityLabelsArray,
} from "../shared/utils/entityLabelFilter";
import { apiFetch } from "../shared/api/apiFetch";
const uiConfig = require('../uiConfig');
const { getRepeatLabel } = require('../shared/utils/repeatOptions');
const RepeatPatternPicker = require('../components/RepeatPatternPicker').default;
const { normalizeRepeatRule } = require('../shared/utils/recurrence');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const { getUserDisplayName } = require('../shared/utils/userDisplay');
const { fetchTablePreferences, saveTablePreferences } = require('../shared/api/tablePreferences');
const { PAGE_VIEW_PREFERENCE_KEYS } = require('../shared/api/pagePreferences');
const { hasSearchParamContext } = require('../shared/utils/pageViewPreferenceState');
const {
  collectTaskListPagePreferenceState,
  applyTaskListPagePreferenceState,
  sanitizeTaskAdvancedFilterCriteria,
  getDefaultTaskListSubFilterResetPatch,
  countTaskListActiveFilters,
  formatTaskListActiveFilterSummary,
} = require('../shared/utils/taskListPagePreference');
const { getTaskAdvancedFilterFieldDefs } = require('../shared/utils/taskAdvancedFilterFields');
const { getApiRoleFromUserInfo } = require('../shared/utils/apiRoleFromUserInfo');
const { isActiveAdvancedFilterValue, patchAdvancedFilterCriteria } = require('../shared/utils/advancedFilter');
const {
  fetchEarliestOpenTimeSlot,
  createTimeBlock,
  fetchDeleteTaskImpact,
  formatDeleteTaskImpactSummary,
  bulkUpdateTasks,
  renameTaskLabel,
  removeTaskLabel,
} = require('../shared/api/tasks');
const ErrorBanner = require('../components/ErrorBanner').default;

const AUTO_SCHEDULE_SEARCH_DAYS = 84;
const TASK_TABLE_PREFERENCE_KEY = 'task.table.v2';
const TASK_TABLE_LEGACY_PREFERENCE_KEY = 'task.table';

/** Collapse legacy `schedule_slot` / `schedule_status` into one `schedule` column (saved prefs). */
function normalizeTaskTableColumnIds(ids) {
  if (!Array.isArray(ids)) return [];
  const out = [];
  let scheduleAdded = false;
  for (const id of ids) {
    if (id === 'schedule_slot' || id === 'schedule_status' || id === 'schedule') {
      if (!scheduleAdded) {
        out.push('schedule');
        scheduleAdded = true;
      }
    } else {
      out.push(id);
    }
  }
  return out;
}

/** Keep Created + Completed paired so headers/cells align (fixes saved prefs with only one of the two). */
function ensureCreatedCompletedColumns(ids) {
  const out = [...ids];
  if (out.includes('completed_date') && !out.includes('created_date')) {
    const i = out.indexOf('completed_date');
    out.splice(i, 0, 'created_date');
  }
  if (out.includes('created_date') && !out.includes('completed_date')) {
    const i = out.indexOf('created_date');
    out.splice(i + 1, 0, 'completed_date');
  }
  return out;
}

/** Prefixed Seq# for review sort when missing from saved column prefs. */
function ensureSeqColumn(ids) {
  if (!Array.isArray(ids) || ids.includes('seq')) return ids;
  return ['seq', ...ids];
}

/** Query params for Dashboard / deep-link chips; removed by "Clear link filters". */
const LINK_CONTEXT_FILTER_PARAM_KEYS = [
  'projectId',
  'project',
  'category',
  'timePhase',
  'vendor',
  'priority',
  'scheduleStatus',
  'scheduleDate',
  'owner',
  'status',
  'navView',
  'navDate',
];

const PROJECT_LINK_CONTEXT_PARAM_KEYS = ['projectId', 'project'];

function Task() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, loading: authLoading, hasRole, hasPermission } = useAuth();
  const { workingHoursStart, workingHoursEnd } = useEffectiveWorkingHours();
  const taskDueTimeOptions = useMemo(
    () => buildTimeOptionsForWorkingStart(workingHoursStart),
    [workingHoursStart],
  );
  const account_id = userInfo?.account_id;

  // set state
  const [CurrentObjs, setCurrentObjs] = useState([]);
  const [ProjectObjs, setProjectObjs] = useState([]);
  const [UserObjs, setUserObjs] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [bannerError, setBannerError] = useState('');

  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [_id, setID] = useState([]);
  const [name, setName] = useState('');
  const [due_date, setDueDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [due_time, setDueTime] = useState(''); // optional HH:mm (24h); empty = date-only (00:00)
  const [repeatRule, setRepeatRule] = useState(null);
  const [amount, setAmount] = useState(0);
  const [priority, setPriority] = useState('Low');
  const [notes, setNotes] = useState('');
  const [owner, setOwner] = useState(() => (userInfo?._id || '')); // user _id; '' = Unassigned; default current user
  /** Selected project _id for top-of-page edit form (not display name). */
  const [editProjectId, setEditProjectId] = useState('');
  const [edit, setEdit] = useState(false);
  const [formExpanded, setFormExpanded] = useState(false); // Track if form section is expanded (edit from calendar)
  const editFormRef = useRef(null);
  const taskPageHeaderBulkStartRef = useRef(null);
  const prevSelectedVisibleCountRef = useRef(0);
  // view mode uses icon toggle; no dropdown ref needed

  // Added for multi-select delete / close / update
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkLabelsOpen, setBulkLabelsOpen] = useState(false);
  const [labelUpdating, setLabelUpdating] = useState(false);

  const {
    applied: advancedFilterApplied,
    setApplied: setAdvancedFilterApplied,
    clearApplied: clearAdvancedFilterApplied,
  } = useAdvancedFilter();
  const {
    activeLabel,
    setActiveLabel,
    labelOptions,
    filteredItems: labelFilteredTaskObjs,
    labelsForNewItem,
    addLabelName,
    renameLabelName,
    removeLabelName,
  } = useEntityLabelFilter({ items: CurrentObjs });
  const [taskStatusActionIds, setTaskStatusActionIds] = useState(() => new Set());

  const [tbAutoScheduleDialog, setTbAutoScheduleDialog] = useState({
    open: false,
    task: null,
    start: null,
    end: null,
  });
  const [tbAutoScheduleAlert, setTbAutoScheduleAlert] = useState({ open: false, title: '', message: '' });

  // Single search term (matches any field)
  const [searchTerm, setSearchTerm] = useState('');

  const currentUserId = userInfo?._id || '';
  const userRolesKey = Array.isArray(userInfo?.roles) ? userInfo.roles.join('|') : '';
  const apiRole = useMemo(
    () => getApiRoleFromUserInfo(userInfo),
    [userInfo?.role, userRolesKey]
  );

  // Category filter: multi-select from links / saved view; default All (empty set)
  const [categoryFilter, setCategoryFilter] = useState(new Set());

  // Project filter: multi-select from links / saved view; default All (empty set)
  const [projectFilter, setProjectFilter] = useState(new Set());

  // Priority filter: from Dashboard drilldown; default All (empty set)
  const [priorityFilter, setPriorityFilter] = useState(new Set());

  // Schedule status filter: from Dashboard drilldown (URL scheduleStatus=…)
  const [scheduleStatusFilter, setScheduleStatusFilter] = useState(new Set());

  // Schedule date filter: from Dashboard Time Blocking daily drilldown (URL scheduleDate=YYYY-MM-DD)
  const [scheduleDateFilter, setScheduleDateFilter] = useState('');

  // Open/Closed filter is separate from the global time navigator (synced with advanced filter Status).
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'open' | 'closed'

  const [vendorFilter, setVendorFilter] = useState(new Set());

  // Added for sorting
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  useEffect(() => {
    if (sortColumn === 'schedule_slot' || sortColumn === 'schedule_status') {
      setSortColumn('schedule');
    }
  }, [sortColumn]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeLabel]);

  // View mode: 'table' | 'calendar'
  const [viewMode, setViewMode] = useState('card');
  const navViewFromUrl = (searchParams.get('navView') || '').trim().toLowerCase();
  const navDateFromUrl = (searchParams.get('navDate') || '').trim();
  const statusFromUrl = (searchParams.get('status') || '').trim().toLowerCase();
  const timeNav = useGlobalTimeNavigator({
    initialView: ['day', 'week', 'month', 'future', 'all'].includes(navViewFromUrl) ? navViewFromUrl : 'all',
    initialFocusDateYmd: navDateFromUrl || undefined,
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    /** "confirm" = Cancel + Confirm; "alert" = OK only (e.g. delete completed, info) */
    mode: undefined,
  });

  const showAlertDialog = useCallback((title, message) => {
    setConfirmDialog({
      open: true,
      title: title || 'Info',
      message: message || '',
      mode: 'alert',
      onConfirm: null,
    });
  }, []);

  const setStatusFilterSynced = useCallback((next) => {
    const v = next === 'open' || next === 'closed' ? next : 'all';
    setStatusFilter(v);
  }, []);

  const handleAdvancedFilterFieldChange = useCallback(
    (fieldId, value) => {
      setAdvancedFilterApplied((prev) =>
        sanitizeTaskAdvancedFilterCriteria(patchAdvancedFilterCriteria(prev, fieldId, value))
      );
    },
    [setAdvancedFilterApplied]
  );

  const handleClearPanelFilters = useCallback(() => {
    const patch = getDefaultTaskListSubFilterResetPatch();
    setStatusFilterSynced(patch.statusFilter);
    clearAdvancedFilterApplied();
    setCategoryFilter(patch.categoryFilter);
    setProjectFilter(patch.projectFilter);
    setPriorityFilter(patch.priorityFilter);
    setScheduleStatusFilter(patch.scheduleStatusFilter);
    setVendorFilter(patch.vendorFilter);
    setScheduleDateFilter(patch.scheduleDateFilter);
    timeNav.setView("all");
    timeNav.setFocusDateYmd(dayjs().format("YYYY-MM-DD"));
  }, [
    clearAdvancedFilterApplied,
    setStatusFilterSynced,
    timeNav,
  ]);

  const shouldSkipTaskPagePreferenceLoad = useCallback(
    () => hasSearchParamContext(searchParams, LINK_CONTEXT_FILTER_PARAM_KEYS),
    [searchParams]
  );

  const clearTaskPageFiltersAfterDefaultCleared = useCallback(() => {
    const patch = getDefaultTaskListSubFilterResetPatch();
    setSearchTerm(patch.searchTerm);
    handleClearPanelFilters();
  }, [handleClearPanelFilters]);

  const confirmClearTaskDefaultView = useCallback(
    (proceed) => {
      setConfirmDialog({
        open: true,
        title: 'Reset / Clear View',
        mode: 'confirm',
        message:
          'Revert all filters to the original default state? Your saved default view will be removed.',
        onConfirm: () => {
          if (typeof proceed === 'function') proceed();
        },
      });
    },
    []
  );

  const getProjectList = useCallback(() => {
    return apiFetch(serverUrl + '/getProject', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => {
        setProjectObjs(Array.isArray(data) ? data : []);
        return data;
      })
      .catch(err => setLoadError(getMessageForFetchError(err)));
  }, [account_id, serverUrl]);

  const getDocument = useCallback(() => {
    setLoadError('');
    apiFetch(serverUrl + '/getTask', {
      method: 'POST',
      body: JSON.stringify({ account_id, user_id: userInfo?._id || '', role: apiRole }),
    })
      .then(resp => resp.json())
      .then(data => {
        setCurrentObjs(Array.isArray(data) ? data : []);
        setLoadError('');
      })
      .catch(err => setLoadError(getMessageForFetchError(err)));
  }, [account_id, serverUrl, userInfo?._id, apiRole]);

  // Task detail view (opens when task name is clicked)
  const [detailTaskId, setDetailTaskId] = useState(null);
  const [detailActiveField, setDetailActiveField] = useState('');
  const [detailInitialValues, setDetailInitialValues] = useState(null);
  const [subTasks, setSubTasks] = useState([]);
  const [subTaskForm, setSubTaskForm] = useState({ name: '', due_date: dayjs().format('YYYY-MM-DD'), priority: 'Medium' });
  const [showSubTaskForm, setShowSubTaskForm] = useState(false);
  const [showBulkSubTaskForm, setShowBulkSubTaskForm] = useState(false);
  const [bulkSubTaskInput, setBulkSubTaskInput] = useState('');
  const [bulkSubTaskSubmitting, setBulkSubTaskSubmitting] = useState(false);
  const [editingSubTaskId, setEditingSubTaskId] = useState(null);
  const [subTaskEditDraft, setSubTaskEditDraft] = useState(null);
  const [detailNotes, setDetailNotes] = useState('');
  const [detailVendorId, setDetailVendorId] = useState('');
  const [vendorList, setVendorList] = useState([]);
  const [assetList, setAssetList] = useState([]);


  const TASK_TABLE_COLUMNS = [
    { id: 'seq', label: 'Seq#' },
    { id: 'name', label: 'Name' },
    { id: 'notes', label: 'Notes' },
    { id: 'due_date', label: 'Due Date' },
    { id: 'amount', label: 'Amount($)' },
    { id: 'repeat', label: 'Repeat' },
    { id: 'status', label: 'Status' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'project', label: 'Project' },
    { id: 'category', label: 'Category' },
    { id: 'owner', label: 'Owner' },
    { id: 'created_by', label: 'Created By' },
    { id: 'created_date', label: 'Created' },
    { id: 'completed_date', label: 'Completed' },
    { id: 'priority', label: 'Priority' },
  ];

  /** Default visible columns (table view); project & category available via column picker. */
  const TASK_TABLE_DEFAULT_VISIBLE_COLUMNS = [
    'seq',
    'name',
    'notes',
    'due_date',
    'amount',
    'repeat',
    'status',
    'schedule',
    'priority',
  ];

  // Table column customization (per user, per table)
  const [visibleTaskColumns, setVisibleTaskColumns] = useState(
    () => [...TASK_TABLE_DEFAULT_VISIBLE_COLUMNS],
  );
  const [columnPrefsLoading, setColumnPrefsLoading] = useState(false);
  const [columnPrefsSaving, setColumnPrefsSaving] = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  /** Inline Seq# edit: { taskId, draft } or null */
  const [seqEdit, setSeqEdit] = useState(null);
  const [seqSavingId, setSeqSavingId] = useState(null);
  const seqEditCancelRef = useRef(false);

  // Task detail form state (for modal edit)
  const [detailName, setDetailName] = useState('');
  const [detailDueDate, setDetailDueDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [detailDueTime, setDetailDueTime] = useState('');
  const [detailAmount, setDetailAmount] = useState(0);
  const [detailProjectId, setDetailProjectId] = useState('');
  const [detailRepeatRule, setDetailRepeatRule] = useState(null);
  const [detailPriority, setDetailPriority] = useState('');
  const [detailOwner, setDetailOwner] = useState('');
  const [detailStatus, setDetailStatus] = useState('');

  const othersProjectId = useMemo(() => {
    const o = (ProjectObjs || []).find((p) => (p.name || '').toLowerCase() === 'others');
    return o?._id != null ? String(o._id) : '';
  }, [ProjectObjs]);

  const detailTask = useMemo(() => {
    if (!detailTaskId) return null;
    return (CurrentObjs || []).find((t) => String(t?._id) === String(detailTaskId)) || null;
  }, [CurrentObjs, detailTaskId]);

  const detailCurrentValues = useMemo(() => {
    const detailRepeatNorm = normalizeRepeatRule(detailRepeatRule, detailDueDate);
    return {
      notes: detailNotes || '',
      vendor_id: detailVendorId || '',
      name: detailName || '',
      due_date: detailDueDate || '',
      due_time: detailDueTime || '',
      amount: detailAmount != null ? Number(detailAmount) : 0,
      project_id: detailProjectId || '',
      repeat_rule: detailRepeatNorm,
      priority: detailPriority || '',
      owner: detailOwner || '',
      status: detailStatus || '',
    };
  }, [
    detailNotes,
    detailVendorId,
    detailName,
    detailDueDate,
    detailDueTime,
    detailAmount,
    detailProjectId,
    detailRepeatRule,
    detailPriority,
    detailOwner,
    detailStatus,
  ]);

  const detailHasChanges = useMemo(() => {
    if (!detailInitialValues) return false;
    return Object.keys(detailCurrentValues).some((k) => {
      if (k === 'repeat_rule') {
        return JSON.stringify(detailCurrentValues[k]) !== JSON.stringify(detailInitialValues[k]);
      }
      return detailCurrentValues[k] !== detailInitialValues[k];
    });
  }, [detailCurrentValues, detailInitialValues]);

  const persistDetailChanges = useCallback(async () => {
    if (!account_id || !detailTask) return;

    const dueDateStr = String(detailDueDate || '').trim();
    if (!dueDateStr) {
      setBannerError('Due date is required.');
      return;
    }
    const dateCheck = validateDate(dueDateStr);
    if (!dateCheck.valid) {
      setBannerError(dateCheck.message);
      return;
    }
    const dueDateForApi = getDueDateForApi(dueDateStr, String(detailDueTime || '').trim() || '00:00');

    const nameTrim = String(detailName || '').trim();
    if (!nameTrim) {
      setBannerError('Task name is required.');
      return;
    }

    const effectiveProjectId = String(detailProjectId || '').trim() || othersProjectId || '';

    const resp = await apiFetch(serverUrl + '/updateTask', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        _id: detailTask._id,
        name: nameTrim,
        due_date: dueDateForApi,
        amount: detailAmount != null ? detailAmount : 0,
        ...(effectiveProjectId ? { project_id: effectiveProjectId } : {}),
        repeat_rule: normalizeRepeatRule(detailRepeatRule, detailDueDate),
        priority: detailPriority || '',
        status: detailStatus || '',
        notes: detailNotes,
        vendor_id: detailVendorId || '',
        owner: detailOwner || '',
        role: apiRole,
      }),
    });

    const text = await resp.text();
    if (!resp.ok) {
      setBannerError(text || 'Failed to save.');
      return;
    }

    setDetailInitialValues(detailCurrentValues);
    setDetailActiveField('');
    setBannerError('');
    await getDocument();
  }, [
    getDocument,
    account_id,
    detailTask,
    detailDueDate,
    detailDueTime,
    detailName,
    detailProjectId,
    othersProjectId,
    serverUrl,
    detailAmount,
    detailRepeatRule,
    detailPriority,
    detailStatus,
    detailNotes,
    detailVendorId,
    detailOwner,
    apiRole,
    detailCurrentValues,
  ]);

  const {
    isSaving: detailSaving,
    handleFieldBlur: handleDetailFieldBlur,
    handleFieldCommitKeyDown: handleDetailFieldCommitKeyDown,
  } = useBlurAutoSave({
    hasChanges: detailHasChanges,
    saveFn: persistDetailChanges,
    onExitEdit: () => setDetailActiveField(''),
  });

  const drilldownAppliedRef = useRef(false);
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [smartAddModalOpen, setSmartAddModalOpen] = useState(false);
  const [taskFiltersPanelOpen, setTaskFiltersPanelOpen] = useState(false);
  const [importPanelExpanded, setImportPanelExpanded] = useState(false);

  // Match Dashboard: wait for auth before getTask so Admin role is sent (full account list vs owner-only).
  useEffect(() => {
    if (authLoading) return;
    if (!account_id || !currentUserId) return;
    getDocument();
    getProjectList();
    getUsers();
    // Primitive deps only — avoid loop from unstable callback refs (getUsers was recreated each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, account_id, currentUserId, apiRole]);

  useEffect(() => {
    const loadPrefs = async () => {
      if (!account_id || !currentUserId) return;
      try {
        setColumnPrefsLoading(true);
        let saved = await fetchTablePreferences(account_id, currentUserId, TASK_TABLE_PREFERENCE_KEY);
        let migratedLegacyPreference = false;
        if (!Array.isArray(saved) || saved.length === 0) {
          const legacySaved = await fetchTablePreferences(account_id, currentUserId, TASK_TABLE_LEGACY_PREFERENCE_KEY);
          if (Array.isArray(legacySaved) && legacySaved.length > 0) {
            saved = legacySaved.filter((id) => id !== 'category');
            migratedLegacyPreference = true;
          }
        }
        if (Array.isArray(saved) && saved.length > 0) {
          const allowedIds = new Set(TASK_TABLE_COLUMNS.map((c) => c.id));
          let filtered = normalizeTaskTableColumnIds(saved).filter((id) => allowedIds.has(id));
          if (filtered.length > 0) {
            filtered = ensureSeqColumn(ensureCreatedCompletedColumns(filtered));
            setVisibleTaskColumns(filtered);
            if (migratedLegacyPreference) {
              await saveTablePreferences(account_id, currentUserId, TASK_TABLE_PREFERENCE_KEY, filtered);
            }
          }
        }
      } catch (err) {
        // ignore errors and fall back to defaults
      } finally {
        setColumnPrefsLoading(false);
      }
    };
    loadPrefs();
  }, [account_id, currentUserId]);

  // Strip inactive/legacy advanced-filter values (e.g. priority "all") so criteria never zero out the list.
  useEffect(() => {
    setAdvancedFilterApplied((prev) => {
      const clean = sanitizeTaskAdvancedFilterCriteria(prev);
      const prevObj = prev && typeof prev === 'object' ? prev : {};
      const same =
        Object.keys(prevObj).length === Object.keys(clean).length &&
        Object.entries(prevObj).every(([k, v]) => clean[k] === v);
      return same ? prev : clean;
    });
  }, [advancedFilterApplied, setAdvancedFilterApplied]);

  // Saved advanced filter may reference a deleted project — drop stale project_id so the list is not empty.
  useEffect(() => {
    const pid = advancedFilterApplied?.project_id;
    if (!isActiveAdvancedFilterValue(pid)) return;
    if (!Array.isArray(ProjectObjs) || ProjectObjs.length === 0) return;
    const exists = ProjectObjs.some((p) => p?._id != null && String(p._id) === String(pid));
    if (exists) return;
    setAdvancedFilterApplied((prev) => {
      if (!prev || String(prev.project_id || '') !== String(pid)) return prev;
      const next = { ...prev };
      delete next.project_id;
      return next;
    });
  }, [ProjectObjs, advancedFilterApplied?.project_id, setAdvancedFilterApplied]);

  // Fetch vendors when detail panel is open (for Pick Vendor) or when filtering by vendor (e.g. drilldown with "No vendor")
  useEffect(() => {
    if (!account_id) {
      setVendorList([]);
      return;
    }
    apiFetch(serverUrl + '/listVendors', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(r => r.json())
      .then(data => setVendorList(Array.isArray(data) ? data : []))
      .catch(() => setVendorList([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [account_id, detailTaskId]);

  // Fetch assets for Project header Asset label / dropdown (and other project-linked UI).
  useEffect(() => {
    if (!account_id) {
      setAssetList([]);
      return;
    }
    apiFetch(serverUrl + '/listAssets', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((data) => setAssetList(Array.isArray(data) ? data : []))
      .catch(() => setAssetList([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [account_id]);

  // When a category filter is active, drop project names that are not in that category.
  const projectOptionsForDropdown = useMemo(() => {
    const hasOthers = (ProjectObjs || []).some(p => (p.name || '').toLowerCase() === 'others');
    const projectList = hasOthers ? (ProjectObjs || []) : [{ name: 'Others' }, ...(ProjectObjs || [])];
    let namesFromProjects = projectList.map(p => p.name);
    let namesFromTasks = (CurrentObjs || []).map(t => (t.project || '').trim()).filter(Boolean);
    if (categoryFilter.size > 0) {
      const catSet = categoryFilter;
      namesFromProjects = projectList
        .filter(p => catSet.has((p.category || 'General').trim()))
        .map(p => p.name);
      namesFromTasks = (CurrentObjs || [])
        .filter(t => catSet.has((t.category || 'General').trim()))
        .map(t => (t.project || '').trim())
        .filter(Boolean);
    }
    return [...new Set([...namesFromProjects, ...namesFromTasks])].sort();
  }, [ProjectObjs, CurrentObjs, categoryFilter]);

  const projectIdForUnambiguousProjectName = useCallback(
    (projectName) => {
      const n = projectName != null && String(projectName).trim() !== '' ? String(projectName).trim() : '';
      if (!n || !Array.isArray(ProjectObjs) || ProjectObjs.length === 0) return undefined;
      const matches = ProjectObjs.filter((p) => (p?.name || '').trim() === n);
      if (matches.length !== 1) return undefined;
      const id = matches[0]._id;
      return id != null ? String(id) : undefined;
    },
    [ProjectObjs]
  );

  /** Resolve project Mongo id for API: prefer explicit id, then unique name match, then previous task's project_id. */
  const resolveProjectIdForPayload = useCallback(
    (projectIdOrName, previousTask) => {
      const raw = projectIdOrName != null ? String(projectIdOrName).trim() : '';
      if (raw && ProjectObjs.some((p) => String(p._id) === raw)) return raw;
      const byName = projectIdForUnambiguousProjectName(projectIdOrName);
      if (byName) return byName;
      if (previousTask?.project_id) return String(previousTask.project_id);
      return undefined;
    },
    [ProjectObjs, projectIdForUnambiguousProjectName]
  );

  // When category filter changes, remove any selected projects that are no longer in the narrowed list
  useEffect(() => {
    if (projectOptionsForDropdown.length === 0) return;
    setProjectFilter(prev => {
      if (prev.size === 0) return prev;
      const allowed = new Set(projectOptionsForDropdown);
      const next = new Set([...prev].filter(p => allowed.has(p)));
      return next.size === prev.size ? prev : next;
    });
  }, [categoryFilter, projectOptionsForDropdown]);

  // Open task detail when linked from Time Blocking (Task?taskId=...)
  const taskDetailFromUrlAppliedRef = useRef(false);
  useEffect(() => {
    if (taskDetailFromUrlAppliedRef.current) return;
    const taskIdFromUrl = searchParams.get('taskId');
    if (!taskIdFromUrl || !Array.isArray(CurrentObjs) || CurrentObjs.length === 0) return;
    const task = CurrentObjs.find((t) => String(t._id) === String(taskIdFromUrl));
    if (!task) return;
    taskDetailFromUrlAppliedRef.current = true;
    openTaskDetail(task);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot open when list first contains the linked task
  }, [searchParams, CurrentObjs]);

  // From Project Management: /Task?projectId=… — reset filters; list is narrowed by project_id (see filteredByProject below)
  const projectIdFromUrl = searchParams.get('projectId');
  useEffect(() => {
    if (!projectIdFromUrl || !String(projectIdFromUrl).trim()) return;
    drilldownAppliedRef.current = true;
    setStatusFilter('all');
    setCategoryFilter(new Set());
    setProjectFilter(new Set());
    setPriorityFilter(new Set());
    setScheduleStatusFilter(new Set());
    setVendorFilter(new Set());
    setSearchTerm('');
  }, [projectIdFromUrl]);

  // Apply drilldown context from Dashboard (URL params) once on mount
  useEffect(() => {
    if (drilldownAppliedRef.current) return;
    const timePhaseRaw = searchParams.get('timePhase');
    const statusRaw = searchParams.get('status');
    const navViewRaw = searchParams.get('navView');
    const navDateRaw = searchParams.get('navDate');
    const project = searchParams.get('project');
    const category = searchParams.get('category');
    const vendor = searchParams.get('vendor');
    const priority = searchParams.get('priority');
    const scheduleStatus = searchParams.get('scheduleStatus');
    const scheduleDate = searchParams.get('scheduleDate');
    const owner = searchParams.get('owner');
    if (
      !timePhaseRaw &&
      !project &&
      !category &&
      priority == null &&
      vendor == null &&
      scheduleStatus == null &&
      (scheduleDate == null || String(scheduleDate).trim() === '') &&
      (owner == null || String(owner).trim() === '')
    ) {
      return;
    }
    drilldownAppliedRef.current = true;

    const statusTrim = statusRaw != null ? String(statusRaw).trim().toLowerCase() : '';
    if (statusTrim === 'open' || statusTrim === 'closed') {
      setStatusFilter(statusTrim);
    }
    const vTrim = navViewRaw != null ? String(navViewRaw).trim().toLowerCase() : '';
    if (['day', 'week', 'month', 'future', 'all'].includes(vTrim)) timeNav.setView(vTrim);
    const dTrim = navDateRaw != null ? String(navDateRaw).trim() : '';
    if (dTrim) timeNav.setFocusDateYmd(dTrim);

    const timePhaseTrim = timePhaseRaw != null ? String(timePhaseRaw).trim().toLowerCase() : '';
    if (timePhaseTrim) {
      // Back-compat: old drilldowns used timePhase to convey open/closed; global time is now controlled by the navigator.
      const parts = new Set(timePhaseTrim.split(',').map((x) => x.trim()).filter(Boolean));
      if (parts.has('closed')) setStatusFilter('closed');
      else if (parts.has('upcoming') || parts.has('remaining')) setStatusFilter('open');
    }
    if (project) setProjectFilter(new Set([project]));
    if (category) setCategoryFilter(new Set([category]));
    if (priority != null) setPriorityFilter(new Set([priority]));
    if (scheduleStatus != null) {
      const schedLabel = scheduleStatusParamToLabel(scheduleStatus);
      if (schedLabel) setScheduleStatusFilter(new Set([schedLabel]));
    }
    if (scheduleDate != null && String(scheduleDate).trim() !== '') {
      setScheduleDateFilter(String(scheduleDate).trim());
    }
    if (vendor != null) {
      // __none__ = no valid vendor (unassigned or deleted/orphaned)
      const vendorId = vendor === '__none__' ? '__no_valid_vendor__' : vendor;
      setVendorFilter(new Set([vendorId]));
    }
  }, [searchParams, timeNav]);

  /** Same behavior as "Clear link filters" on URL + drill-down filter state. */
  function applyClearLinkFilters(options = {}) {
    const { keepProject = false } = options;
    const params = new URLSearchParams(searchParams);
    for (const k of LINK_CONTEXT_FILTER_PARAM_KEYS) {
      if (keepProject && PROJECT_LINK_CONTEXT_PARAM_KEYS.includes(k)) continue;
      params.delete(k);
    }
    params.delete('timePhase');
    setSearchParams(params, { replace: true });

    setScheduleStatusFilter(new Set());
    setScheduleDateFilter('');
    setCategoryFilter(new Set());
    setPriorityFilter(new Set());
    setVendorFilter(new Set());
    if (!keepProject) setProjectFilter(new Set());
    setStatusFilter('all');
    drilldownAppliedRef.current = false;
  }

  /** Equivalent to "Clear link filters" except keeps `projectId` / `project` on the URL and `projectFilter` state. */
  function clearLinkFiltersKeepProject() {
    applyClearLinkFilters({ keepProject: true });
  }

  /** Same URL filter as Project list → Tasks and task detail “View project”: `projectId`, clear `taskId`. */
  const applyProjectIdUrlFilter = useCallback((projectId) => {
    const raw = projectId != null ? String(projectId).trim() : '';
    if (!raw) return;
    setDetailTaskId(null);
    const params = new URLSearchParams(searchParams);
    params.set('projectId', raw);
    params.delete('taskId');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  /**
   * Chips for the “Viewing tasks with” bar: deep-link / drill-down context only (project, schedule, etc.).
   * Time phase is not listed here — it is controlled by the toolbar and may still be in the URL for sharing/bookmarks.
   */
  const linkContextFilters = useMemo(() => {
    const items = [];
    const sp = new URLSearchParams(location.search);
    const pidRaw = sp.get('projectId');
    const pidTrim = pidRaw != null && String(pidRaw).trim() ? String(pidRaw).trim() : '';
    if (pidTrim) {
      const p = (ProjectObjs || []).find((x) => String(x._id) === pidTrim);
      items.push({ id: 'projectId', text: `Project: ${p?.name || 'Unknown project'}` });
    }
    const projectNameRaw = sp.get('project');
    const projectNameTrim = projectNameRaw != null && String(projectNameRaw).trim() ? String(projectNameRaw).trim() : '';
    if (projectNameTrim && !pidTrim) {
      items.push({ id: 'project', text: `Project: ${projectNameTrim}` });
    }
    const categoryRaw = sp.get('category');
    const categoryTrim = categoryRaw != null && String(categoryRaw).trim() ? String(categoryRaw).trim() : '';
    if (categoryTrim) {
      items.push({ id: 'category', text: `Category: ${categoryTrim}` });
    }
    const priorityRaw = sp.get('priority');
    if (priorityRaw != null && String(priorityRaw).trim() !== '') {
      items.push({ id: 'priority', text: `Priority: ${String(priorityRaw).trim()}` });
    }
    const scheduleStatusRaw = sp.get('scheduleStatus');
    if (scheduleStatusRaw != null && String(scheduleStatusRaw).trim() !== '') {
      const label = scheduleStatusParamToLabel(String(scheduleStatusRaw).trim());
      if (label) items.push({ id: 'scheduleStatus', text: `Schedule: ${label}` });
    }
    const scheduleDateRaw = sp.get('scheduleDate');
    if (scheduleDateRaw != null && String(scheduleDateRaw).trim() !== '') {
      items.push({ id: 'scheduleDate', text: `Scheduled: ${String(scheduleDateRaw).trim()}` });
    }
    const vendorRaw = sp.get('vendor');
    if (vendorRaw != null && String(vendorRaw).trim() !== '') {
      const ven = String(vendorRaw).trim();
      if (ven === '__none__') {
        items.push({ id: 'vendor', text: 'Vendor: None / unassigned' });
      } else {
        const v = (vendorList || []).find((x) => String(x._id) === ven);
        items.push({
          id: 'vendor',
          text: v ? `Vendor: ${(v.name || 'Vendor') + (v.type ? ` (${v.type})` : '')}` : `Vendor: ${ven}`,
        });
      }
    }
    const ownerRaw = sp.get('owner');
    const ownerTrim = ownerRaw != null && String(ownerRaw).trim() ? String(ownerRaw).trim() : '';
    if (ownerTrim) {
      if (ownerTrim === '__unassigned__') {
        items.push({ id: 'owner', text: 'Owner: Unassigned' });
      } else {
        const u = (UserObjs || []).find((x) => String(x._id) === ownerTrim);
        items.push({
          id: 'owner',
          text: `Owner: ${u ? getUserDisplayName(u) || ownerTrim : ownerTrim}`,
        });
      }
    }
    return items;
  }, [location.search, ProjectObjs, vendorList, UserObjs]);

  function clearLinkContextFilters() {
    applyClearLinkFilters({ keepProject: false, nextTimePhaseSet: new Set() });
  }

  // Open Add Task modal when coming from menu (Task?add=1)
  useEffect(() => {
    const add = searchParams.get('add');
    if (add === '1') {
      setAddTaskModalOpen(true);
    }
  }, [searchParams]);

  // Open Smart Add modal when coming from menu (Task?smart=1)
  useEffect(() => {
    const smart = searchParams.get('smart');
    if (smart === '1') {
      setSmartAddModalOpen(true);
    }
  }, [searchParams]);

  const taskAdvancedFilterFields = useMemo(
    () => getTaskAdvancedFilterFieldDefs(ProjectObjs || []),
    [ProjectObjs]
  );

  const taskListActiveFilterSource = useMemo(
    () => ({
      statusFilter,
      categoryFilter,
      projectFilter,
      priorityFilter,
      scheduleStatusFilter,
      vendorFilter,
      scheduleDateFilter,
      advancedFilter: advancedFilterApplied,
      navView: timeNav.view,
    }),
    [
      statusFilter,
      categoryFilter,
      projectFilter,
      priorityFilter,
      scheduleStatusFilter,
      vendorFilter,
      scheduleDateFilter,
      advancedFilterApplied,
      timeNav.view,
    ]
  );

  const taskListActiveFilterCount = useMemo(
    () => countTaskListActiveFilters(taskListActiveFilterSource),
    [taskListActiveFilterSource]
  );

  const taskListActiveFilterSummary = useMemo(
    () =>
      formatTaskListActiveFilterSummary(taskListActiveFilterSource, {
        advancedFields: taskAdvancedFilterFields,
      }),
    [taskListActiveFilterSource, taskAdvancedFilterFields]
  );

  const getUsers = useCallback(() => {
    return apiFetch(serverUrl + '/getUser', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => {
        setUserObjs(data || []);
        return data;
      })
      .catch(err => setLoadError(getMessageForFetchError(err)));
  }, [account_id, serverUrl]);

  function getSubTasks(taskId) {
    if (!taskId) return;
    apiFetch(serverUrl + '/getSubTasks', {
      method: 'POST',
      body: JSON.stringify({ account_id, task_id: taskId }),
    })
      .then(resp => resp.json())
      .then(data => setSubTasks(Array.isArray(data) ? data : []))
      .catch(() => setSubTasks([]));
  }

  function getDetailFormValuesFromTask(task) {
    const dueDatePart = getDueDatePart(task?.due_date) || dayjs().format('YYYY-MM-DD');
    return {
      notes: task?.notes || '',
      vendor_id: task?.vendor_id || '',
      name: task?.name || '',
      due_date: dueDatePart,
      due_time: getDueTimePart(task?.due_date) || '',
      amount: task?.amount != null ? task.amount : 0,
      project_id: task?.project_id || '',
      // category is derived from Project -> Asset -> category (server returns it for display)
      repeat_rule: normalizeRepeatRule(task?.repeat_rule ?? task?.repeat, dueDatePart),
      priority: task?.priority != null && String(task.priority).trim() !== '' ? String(task.priority).trim() : '',
      owner: task?.owner || '',
      status: task?.status || '',
    };
  }

  function applyDetailFormValues(values) {
    setDetailNotes(values.notes || '');
    setDetailVendorId(values.vendor_id || '');
    setDetailName(values.name || '');
    setDetailDueDate(values.due_date || dayjs().format('YYYY-MM-DD'));
    setDetailDueTime(values.due_time || '');
    setDetailAmount(values.amount != null ? values.amount : 0);
    setDetailProjectId(values.project_id || '');
    setDetailRepeatRule(values.repeat_rule != null ? values.repeat_rule : null);
    setDetailPriority(values.priority || '');
    setDetailOwner(values.owner || '');
    setDetailStatus(values.status || '');
  }

  function openTaskDetail(task) {
    if (!task) return;
    const initialValues = getDetailFormValuesFromTask(task);
    setDetailTaskId(task._id);
    setEditingSubTaskId(null);
    setSubTaskEditDraft(null);
    setShowSubTaskForm(false);
    setShowBulkSubTaskForm(false);
    setBulkSubTaskInput('');
    setDetailActiveField('');
    setDetailInitialValues(initialValues);
    applyDetailFormValues(initialValues);
    getSubTasks(task._id);
  }

  function updateDocument() {
    // Validate due date is provided and valid
    const dueDateStr = (due_date != null && typeof due_date === 'string') ? due_date.trim() : '';
    if (!dueDateStr) {
      setConfirmDialog({ open: true, title: 'Task', message: 'Please select a due date', onConfirm: null, mode: 'alert' });
      return Promise.resolve(false);
    }
    const dateCheck = validateDate(dueDateStr);
    if (!dateCheck.valid) { setConfirmDialog({ open: true, title: 'Task', message: dateCheck.message, onConfirm: null, mode: 'alert' }); return Promise.resolve(false); }
    const dueDateForApi = getDueDateForApi(dueDateStr, due_time && due_time.trim() ? due_time.trim() : '00:00');

    // Validate name is provided
    const nameStr = (name != null && typeof name === 'string') ? name.trim() : '';
    if (!nameStr) {
      setConfirmDialog({ open: true, title: 'Task', message: 'Please enter a name for the task', onConfirm: null, mode: 'alert' });
      return Promise.resolve(false);
    }

    const effectiveProjectId = String(editProjectId || '').trim() || othersProjectId || '';

    return apiFetch(serverUrl + '/updateTask', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        _id,
        name,
        due_date: dueDateForApi,
        amount,
        ...(effectiveProjectId ? { project_id: effectiveProjectId } : {}),
        repeat_rule: normalizeRepeatRule(repeatRule, due_date),
        priority,
        notes,
        owner: owner || '',
        role: apiRole,
      }),
    })
      .then(response => response.text().then(text => ({ ok: response.ok, text })))
      .then(({ ok, text }) => {
        setConfirmDialog({ open: true, title: 'Task', message: text, onConfirm: null, mode: 'alert' });
        if (ok) {
          getDocument();
          return true;
        }
        return false;
      })
      .catch(err => {
        setBannerError(getMessageForFetchError(err));
        return false;
      });
  }

  function parseSeqDraft(raw) {
    const s = raw == null ? '' : String(raw).trim();
    if (s === '') return { ok: true, value: undefined };
    if (!/^\d+$/.test(s)) return { ok: false, error: 'Seq# must be a whole number (0 or greater).' };
    const n = Number(s);
    if (!Number.isInteger(n) || n < 0) return { ok: false, error: 'Seq# must be a whole number (0 or greater).' };
    return { ok: true, value: n };
  }

  function startSeqEdit(task, e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (seqSavingId) return;
    seqEditCancelRef.current = false;
    setSeqEdit({
      taskId: task._id,
      draft: task.seq != null && task.seq !== '' ? String(task.seq) : '',
    });
  }

  function cancelSeqEdit() {
    seqEditCancelRef.current = true;
    setSeqEdit(null);
  }

  async function commitSeqEdit(task) {
    if (seqEditCancelRef.current) {
      seqEditCancelRef.current = false;
      return;
    }
    if (!seqEdit || String(seqEdit.taskId) !== String(task._id)) return;
    if (seqSavingId) return;

    const parsed = parseSeqDraft(seqEdit.draft);
    if (!parsed.ok) {
      setBannerError(parsed.error);
      return;
    }

    const prevSeq = task.seq != null && task.seq !== '' ? Number(task.seq) : undefined;
    const nextSeq = parsed.value;
    const unchanged =
      (prevSeq === undefined && nextSeq === undefined) ||
      (prevSeq !== undefined && nextSeq !== undefined && prevSeq === nextSeq);
    if (unchanged) {
      setSeqEdit(null);
      return;
    }

    const nameStr = (task.name != null && typeof task.name === 'string') ? task.name.trim() : '';
    if (!nameStr) {
      setBannerError('Task name is required.');
      return;
    }
    const dueForApi = task.due_date;
    if (dueForApi == null || dueForApi === '') {
      setBannerError('Due date is required.');
      return;
    }

    const effectiveProjectId =
      (task.project_id != null && String(task.project_id).trim()) ||
      othersProjectId ||
      '';

    setSeqSavingId(task._id);
    setBannerError('');
    try {
      const resp = await apiFetch(serverUrl + '/updateTask', {
        method: 'POST',
        body: JSON.stringify({
          account_id,
          _id: task._id,
          name: nameStr,
          due_date: dueForApi,
          amount: task.amount != null ? task.amount : 0,
          ...(effectiveProjectId ? { project_id: effectiveProjectId } : {}),
          repeat_rule: task.repeat_rule != null ? task.repeat_rule : null,
          priority: task.priority || '',
          notes: task.notes != null ? task.notes : '',
          owner: task.owner || '',
          role: apiRole,
          seq: nextSeq === undefined ? '' : nextSeq,
        }),
      });
      const text = await resp.text();
      if (!resp.ok) {
        setBannerError(text || 'Failed to save Seq#.');
        return;
      }
      setCurrentObjs((prev) =>
        (prev || []).map((t) => {
          if (String(t._id) !== String(task._id)) return t;
          const next = { ...t };
          if (nextSeq === undefined) delete next.seq;
          else next.seq = nextSeq;
          return next;
        }),
      );
      setSeqEdit(null);
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setSeqSavingId(null);
    }
  }

  function deleteDocument(currentObj) {
    const { _id } = currentObj;
    apiFetch(serverUrl + '/deleteTask', {
      method: 'POST',
      body: JSON.stringify({ account_id, _id }),
    })
      .then(async (response) => {
        const data = await response.text();
        if (!response.ok) {
          setBannerError(data || 'Failed to delete task');
          return;
        }
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(_id);
        setSelectedIds(newSelectedIds);
        if (detailTaskId && String(detailTaskId) === String(_id)) {
          setDetailTaskId(null);
        }
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, mode: undefined });
        getDocument();
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function duplicateDocument(currentObj) {
    const { name, due_date, amount, repeat_rule, priority, notes, owner, seq } = currentObj;
    const duplicateName = buildDuplicateLabel(name, { fallback: 'Task' });
    const dueStr = getDueDateForInput(due_date) || getDueDateForApi(dayjs().format('YYYY-MM-DD'), '00:00');
    const resolvedProjectId = resolveProjectIdForPayload(currentObj.project_id || currentObj.project, currentObj);
    const duplicatedLabels = getItemLabels(currentObj);
    const createBody = {
      account_id,
      name: duplicateName,
      due_date: dueStr,
      amount: amount != null ? amount : 0,
      repeat_rule: normalizeRepeatRule(repeat_rule, getDueDatePart(due_date)),
      priority: priority || '',
      notes: notes || '',
      labels: duplicatedLabels,
      owner: owner || '',
      created_by: userInfo?._id || '',
    };
    if (resolvedProjectId) createBody.project_id = resolvedProjectId;
    if (seq != null && seq !== '') {
      const seqNum = Number(seq);
      if (Number.isInteger(seqNum) && seqNum >= 0) createBody.seq = seqNum;
    }
    apiFetch(serverUrl + '/createTask', {
      method: 'POST',
      body: JSON.stringify(createBody),
    })
      .then(async (response) => {
        const data = await response.text();
        if (!response.ok) throw new Error(data || 'Failed to duplicate task');
        const displayLabel = duplicateName.trim() || 'Duplicated task';
        const dueLine = formatDueDate(dueStr) || '—';
        let newId = null;
        try {
          const j = JSON.parse(data);
          if (j && j._id != null) newId = String(j._id);
        } catch {
          /* plain text error path not reached if ok */
        }
        const closeAndOpenDetail = () => {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, mode: undefined });
          if (newId) {
            openTaskDetail({
              _id: newId,
              name: duplicateName,
              due_date: dueStr,
              amount: createBody.amount,
              repeat_rule: createBody.repeat_rule,
              priority: createBody.priority,
              notes: createBody.notes,
              labels: duplicatedLabels,
              owner: createBody.owner,
              project_id: resolvedProjectId || '',
              status: 'Open',
              ...(createBody.seq !== undefined ? { seq: createBody.seq } : {}),
            });
          }
        };
        setConfirmDialog({
          open: true,
          title: 'Task duplicated',
          message:
            newId != null ? (
              <DuplicateSuccessMessage
                linkLabel={displayLabel}
                onOpenDetail={closeAndOpenDetail}
                footer={`Due: ${dueLine}`}
              />
            ) : (
              `Created "${displayLabel}".\n\nDue: ${dueLine}`
            ),
          onConfirm: null,
          mode: 'alert',
        });
        getDocument();
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  async function confirmDeleteOne(currentObj) {
    if (!currentObj?._id) return;
    const label = (currentObj.name || 'this task').trim() || 'this task';
    const displayLabel = label.length > 100 ? `${label.slice(0, 100)}…` : label;
    const dueLine = formatDueDate(currentObj.due_date) || '—';
    let impactSummary = formatDeleteTaskImpactSummary(null);
    try {
      const impact = await fetchDeleteTaskImpact({ account_id, _id: currentObj._id });
      impactSummary = formatDeleteTaskImpactSummary(impact);
    } catch {
      impactSummary = formatDeleteTaskImpactSummary(null);
    }
    setConfirmDialog({
      open: true,
      title: 'Delete task',
      message: `Delete "${displayLabel}"?\n\nDue: ${dueLine}\n\n${impactSummary}\n\nThis cannot be undone.`,
      onConfirm: () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, mode: undefined });
        deleteDocument(currentObj);
      },
    });
  }

  async function openAutoScheduleFromTaskRow(task) {
    if (!task?._id || taskHasTimeBlocks(task)) return;
    if (!account_id || !currentUserId) {
      setBannerError('Sign in to auto-schedule.');
      return;
    }
    const from = new Date();
    const from_calendar_date = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
    const viewer_time_zone =
      typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined;
    let slot;
    try {
      slot = await fetchEarliestOpenTimeSlot({
        account_id,
        user_id: currentUserId,
        from_calendar_date,
        max_days: AUTO_SCHEDULE_SEARCH_DAYS,
        viewer_time_zone,
      });
    } catch (err) {
      setTbAutoScheduleAlert({
        open: true,
        title: 'Auto schedule failed',
        message: getMessageForFetchError(err) || 'Could not search for an open slot. Try again.',
      });
      return;
    }
    if (!slot) {
      setTbAutoScheduleAlert({
        open: true,
        title: 'No open slot found',
        message: `No free 30-minute slot in the next ${Math.round(AUTO_SCHEDULE_SEARCH_DAYS / 7)} weeks during your working hours (and synced calendar, if any).`,
      });
      return;
    }
    setTbAutoScheduleDialog({
      open: true,
      task,
      start: new Date(slot.start_time),
      end: new Date(slot.end_time),
    });
  }

  // eslint-disable-next-line no-unused-vars -- kept for potential single-row close UI
  function closeDocument(currentObj) {
    const { _id } = currentObj;
    apiFetch(serverUrl + '/closeTask', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        _id,
        user_id: userInfo?._id || '',
        viewer_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    })
      .then(response => {
        return response.text();
      })
      .then(data => {
        setConfirmDialog({ open: true, title: 'Close task', message: data, onConfirm: null, mode: 'alert' });
        getDocument();
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    const success = await updateDocument();
    if (success) {
      setID([]);
      setName('');
      setDueDate(dayjs().format('YYYY-MM-DD'));
      setDueTime('');
      setAmount(0);
      setRepeatRule(null);
      setPriority('Low');
      setNotes('');
      setOwner(userInfo?._id || '');
      setEditProjectId('');
      setEdit(false);
      setFormExpanded(false);
    }
  };

  function handleSelectOne(_id, checked) {
    const newSelectedIds = new Set(selectedIds);
    if (checked) {
      newSelectedIds.add(_id);
    } else {
      newSelectedIds.delete(_id);
    }
    setSelectedIds(newSelectedIds);
  }

  const handleSelectAll = useCallback((e, getFilteredRows) => {
    const filteredData = typeof getFilteredRows === 'function' ? getFilteredRows() : [];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (e.target.checked) filteredData.forEach((obj) => next.add(obj._id));
      else filteredData.forEach((obj) => next.delete(obj._id));
      return next;
    });
  }, []);

  async function deleteSelected() {
    const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, sortedTaskObjs);
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: 'Delete tasks', message: 'Please select at least one record to delete', onConfirm: null, mode: 'alert' });
      return;
    }
    const idsToDelete = Array.from(selectedVisibleIds);
    let impactSummary = formatDeleteTaskImpactSummary(null);
    try {
      const impact = await fetchDeleteTaskImpact({ account_id, _ids: idsToDelete });
      impactSummary = formatDeleteTaskImpactSummary(impact);
    } catch {
      impactSummary = formatDeleteTaskImpactSummary(null);
    }
    setConfirmDialog({
      open: true,
      title: "Delete tasks",
      message: `You are about to delete ${idsToDelete.length} selected task(s).\n\n${impactSummary}\n\nThis cannot be undone.`,
      onConfirm: async () => {
        setDeleting(true);

        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        // Delete each record sequentially
        for (let i = 0; i < idsToDelete.length; i++) {
          const _id = idsToDelete[i];

          try {
            const response = await apiFetch(serverUrl + '/deleteTask', {
              method: 'POST',
      body: JSON.stringify({ account_id, _id }),
            });

            const data = await response.text();

            if (response.ok) {
              successCount++;
            } else {
              errorCount++;
              errors.push(`ID ${_id}: ${data}`);
            }
          } catch (error) {
            errorCount++;
            errors.push(`ID ${_id}: ${getMessageForFetchError(error)}`);
          }
        }

        // Show results
        let message = `Delete completed!\nSuccess: ${successCount}\nErrors: ${errorCount}`;
        if (errors.length > 0 && errors.length <= 10) {
          message += '\n\nErrors:\n' + errors.join('\n');
        } else if (errors.length > 10) {
          message += '\n\nFirst 10 errors:\n' + errors.slice(0, 10).join('\n');
        }
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message,
          mode: 'alert',
          onConfirm: () => {
            // Clear selection and refresh the document list
            setSelectedIds(new Set());
            getDocument();
            setDeleting(false);
          },
        });
      },
    });
  }

  async function closeSelected() {
    const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, sortedTaskObjs);
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: 'Close tasks', message: 'Please select at least one record to close', onConfirm: null, mode: 'alert' });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Close tasks",
      message: `Are you sure you want to close ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setClosing(true);
        const idsToClose = Array.from(selectedVisibleIds);
        let successCount = 0;
        let errorCount = 0;
        const errors = [];
        let subTasksModified = 0;
        let timeBlocksModified = 0;
        let subTasksMatched = 0;
        let timeBlocksMatched = 0;
        for (let i = 0; i < idsToClose.length; i++) {
          const _id = idsToClose[i];
          try {
            const response = await apiFetch(serverUrl + '/closeTask', {
              method: 'POST',
              body: JSON.stringify({
                account_id,
                _id,
                user_id: userInfo?._id || '',
                viewer_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              }),
            });
            const ct = (response.headers.get('content-type') || '').toLowerCase();
            const body = ct.includes('application/json') ? await response.json().catch(() => null) : await response.text();
            if (response.ok) {
              successCount++;
              if (body && typeof body === 'object') {
                subTasksMatched += Number(body.subTasksMatched || 0) || 0;
                subTasksModified += Number(body.subTasksModified || 0) || 0;
                timeBlocksMatched += Number(body.timeBlocksMatched || 0) || 0;
                timeBlocksModified += Number(body.timeBlocksModified || 0) || 0;
              }
            } else {
              errorCount++;
              const msg = typeof body === 'string' ? body : (body && body.message ? body.message : '');
              errors.push(`ID ${_id}: ${msg || 'Failed'}`);
            }
          } catch (error) {
            errorCount++;
            errors.push(`ID ${_id}: ${getMessageForFetchError(error)}`);
          }
        }
        setSelectedIds(new Set());
        getDocument();
        setClosing(false);
        if (errorCount > 0) {
          let errMsg =
            `Success: ${successCount} · Errors: ${errorCount}\n\n` +
            formatChildCascadeLines({
              subTasksMatched,
              subTasksModified,
              timeBlocksMatched,
              timeBlocksModified,
            }).join('\n');
          if (errors.length > 0 && errors.length <= 10) errMsg += '\n\n' + errors.join('\n');
          else if (errors.length > 10) errMsg += '\n\nFirst 10 errors:\n' + errors.slice(0, 10).join('\n');
          showAlertDialog('Close completed with errors', errMsg);
        }
      },
    });
  }

  function openBulkUpdate() {
    if (intersectSelectedIdsWithRows(selectedIds, sortedTaskObjs).size === 0) {
      setConfirmDialog({ open: true, title: 'Bulk update', message: 'Please select at least one task to update', onConfirm: null, mode: 'alert' });
      return;
    }
    setBulkUpdateOpen(true);
  }

  async function applyBulkUpdate(fields) {
    const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, sortedTaskObjs);
    if (selectedVisibleIds.size === 0) {
      setBulkUpdateOpen(false);
      return;
    }
    const ids = Array.from(selectedVisibleIds);
    const payload = {
      account_id,
      _ids: ids,
      user_id: userInfo?._id || '',
      role: apiRole,
      ...fields,
    };
    try {
      setBulkUpdating(true);
      const res = await bulkUpdateTasks(payload);
      const matched = res && typeof res.matched === 'number' ? res.matched : ids.length;
      const modified = res && typeof res.modified === 'number' ? res.modified : 0;
      setConfirmDialog({
        open: true,
        title: 'Bulk update completed',
        mode: 'alert',
        onConfirm: null,
        message: `Updated ${modified} task(s) (matched ${matched}).`,
      });
      setSelectedIds(new Set());
      setBulkUpdateOpen(false);
      await getDocument();
    } catch (err) {
      setConfirmDialog({
        open: true,
        title: 'Bulk update failed',
        mode: 'alert',
        onConfirm: null,
        message: err && err.message ? err.message : 'Bulk update failed',
      });
    } finally {
      setBulkUpdating(false);
    }
  }

  function taskLabelScopePayload(extra = {}) {
    return {
      account_id,
      user_id: userInfo?._id || '',
      role: apiRole,
      ...extra,
    };
  }

  function openBulkLabels() {
    if (intersectSelectedIdsWithRows(selectedIds, sortedTaskObjs).size === 0) {
      setConfirmDialog({ open: true, title: 'Update labels', message: 'Please select at least one task to update', onConfirm: null, mode: 'alert' });
      return;
    }
    setBulkLabelsOpen(true);
  }

  function handleAddLabel(name) {
    const result = addLabelName(name);
    if (!result.ok) {
      setConfirmDialog({
        open: true,
        title: 'Add label',
        message: result.error,
        mode: 'alert',
        onConfirm: null,
      });
    }
    return result;
  }

  async function handleRenameLabel(oldLabel, newLabel) {
    setLabelUpdating(true);
    try {
      await renameTaskLabel(taskLabelScopePayload({ oldLabel, newLabel }));
      const result = renameLabelName(oldLabel, newLabel);
      if (!result.ok) throw new Error(result.error || 'Failed to rename label');
      await getDocument();
    } catch (err) {
      setBannerError(err && err.message ? err.message : 'Failed to rename label');
      throw err;
    } finally {
      setLabelUpdating(false);
    }
  }

  async function handleRemoveLabel(label) {
    setLabelUpdating(true);
    try {
      await removeTaskLabel(taskLabelScopePayload({ label }));
      const result = removeLabelName(label);
      if (!result.ok) throw new Error(result.error || 'Failed to remove label');
      await getDocument();
    } catch (err) {
      setBannerError(err && err.message ? err.message : 'Failed to remove label');
      throw err;
    } finally {
      setLabelUpdating(false);
    }
  }

  async function applyLabelsToTaskIds(ids, labels) {
    const payload = taskLabelScopePayload({
      _ids: ids,
      labels: normalizeEntityLabelsArray(labels),
    });
    return bulkUpdateTasks(payload);
  }

  async function handleApplyLabelsForTask(task, labels) {
    if (!task?._id || labelUpdating) return;
    const nextLabels = normalizeEntityLabelsArray(labels);
    if (entityLabelsEqual(getItemLabels(task), nextLabels)) return;
    setLabelUpdating(true);
    try {
      await applyLabelsToTaskIds([task._id], nextLabels);
      await getDocument();
    } catch (err) {
      setBannerError(err && err.message ? err.message : 'Failed to update labels');
      throw err;
    } finally {
      setLabelUpdating(false);
    }
  }

  async function handleBulkApplyLabels(labels) {
    const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, sortedTaskObjs);
    const ids = Array.from(selectedVisibleIds);
    if (ids.length === 0) {
      setBulkLabelsOpen(false);
      return;
    }
    setLabelUpdating(true);
    try {
      const { updated, skipped, errors } = await bulkApplyLabelsToItems({
        items: CurrentObjs,
        selectedIds: selectedVisibleIds,
        labels,
        getId: (item) => item?._id || '',
        updateLabels: async (item, nextLabels) => {
          await applyLabelsToTaskIds([item._id], nextLabels);
        },
      });
      await getDocument();
      if (errors.length > 0) {
        setConfirmDialog({
          open: true,
          title: 'Bulk label update',
          message: `Updated ${updated} task(s) (${skipped} unchanged).\nErrors: ${errors.length}\n\n${errors.slice(0, 10).join('\n')}`,
          mode: 'alert',
          onConfirm: null,
        });
      }
      setSelectedIds(new Set());
      setBulkLabelsOpen(false);
    } catch (err) {
      setBannerError(err && err.message ? err.message : 'Failed to update labels');
      throw err;
    } finally {
      setLabelUpdating(false);
    }
  }

  async function closeOneTask(task) {
    if (!task?._id) return;
    const label = (task.name || 'this task').trim() || 'this task';
    const displayLabel = label.length > 100 ? `${label.slice(0, 100)}…` : label;
    const dueLine = formatDueDate(task.due_date) || '—';
    const _id = task._id;
    let confirmMessage = `Close "${displayLabel}"?\n\nDue: ${dueLine}\n\nNo related sub-tasks or time blocks to close.`;
    try {
      const previewRes = await apiFetch(serverUrl + '/previewCloseTask', {
        method: 'POST',
        body: JSON.stringify({ account_id, _id }),
      });
      if (previewRes.ok) {
        const p = await previewRes.json();
        const st = Number(p.subTasksMatched || 0) || 0;
        const tb = Number(p.timeBlocksMatched || 0) || 0;
        const cascadePreview = formatChildCascadeLines(
          {
            subTasksMatched: st,
            subTasksModified: st,
            timeBlocksMatched: tb,
            timeBlocksModified: tb,
          },
          { future: true, futureContext: 'close' }
        );
        confirmMessage = [`Close "${displayLabel}"?`, '', `Due: ${dueLine}`, '', ...cascadePreview].join('\n');
      }
    } catch {
      confirmMessage = `Close "${displayLabel}"?\n\nDue: ${dueLine}`;
    }
    setConfirmDialog({
      open: true,
      title: "Close task",
      message: confirmMessage,
      onConfirm: async () => {
        setTaskStatusActionIds((prev) => new Set(prev).add(String(_id)));
        try {
          const response = await apiFetch(serverUrl + '/closeTask', {
            method: 'POST',
            body: JSON.stringify({
              account_id,
              _id,
              user_id: userInfo?._id || '',
              viewer_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
          });
          if (response.ok) {
            getDocument();
            if (detailTaskId && String(detailTaskId) === String(_id)) {
              getSubTasks(_id);
            }
          } else {
            const ct = (response.headers.get('content-type') || '').toLowerCase();
            const body = ct.includes('application/json') ? await response.json().catch(() => null) : await response.text();
            const msg = typeof body === 'string' ? body : (body && body.message ? body.message : null);
            showAlertDialog('Close failed', msg || 'Failed to close task');
          }
        } catch (error) {
          setBannerError(getMessageForFetchError(error, 'Failed to close task'));
        } finally {
          setTaskStatusActionIds((prev) => {
            const next = new Set(prev);
            next.delete(String(_id));
            return next;
          });
        }
      },
    });
  }

  async function reopenOneTask(_id) {
    let confirmMessage = 'Reopen this task?\n\nNo related sub-tasks or time blocks to restore.';
    try {
      const previewRes = await apiFetch(serverUrl + '/previewReopenTask', {
        method: 'POST',
        body: JSON.stringify({ account_id, _id }),
      });
      if (previewRes.ok) {
        const p = await previewRes.json();
        const st = Number(p.subTasksMatched || 0) || 0;
        const tb = Number(p.timeBlocksMatched || 0) || 0;
        const cascadePreview = formatChildCascadeLines(
          {
            subTasksMatched: st,
            subTasksModified: st,
            timeBlocksMatched: tb,
            timeBlocksModified: tb,
          },
          { future: true }
        );
        confirmMessage = ['Reopen this task?', '', ...cascadePreview].join('\n');
      }
    } catch {
      confirmMessage = 'Reopen this task?';
    }
    setConfirmDialog({
      open: true,
      title: "Reopen task",
      message: confirmMessage,
      onConfirm: async () => {
        setTaskStatusActionIds((prev) => new Set(prev).add(String(_id)));
        try {
          const response = await apiFetch(serverUrl + '/reopenTask', {
            method: 'POST',
            body: JSON.stringify({ account_id, _id }),
          });
          if (response.ok) {
            getDocument();
            if (detailTaskId && String(detailTaskId) === String(_id)) {
              getSubTasks(_id);
            }
          } else {
            const ct = (response.headers.get('content-type') || '').toLowerCase();
            const body = ct.includes('application/json') ? await response.json().catch(() => null) : await response.text();
            const msg = typeof body === 'string' ? body : (body && body.message ? body.message : null);
            showAlertDialog('Reopen failed', msg || 'Failed to reopen task');
          }
        } catch (error) {
          setBannerError(getMessageForFetchError(error, 'Failed to reopen task'));
        } finally {
          setTaskStatusActionIds((prev) => {
            const next = new Set(prev);
            next.delete(String(_id));
            return next;
          });
        }
      },
    });
  }

  function handleSort(column) {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  }

  function sortData(data, column, direction) {
    const sorted = [...data].sort((a, b) => {
      let aVal, bVal;
      
      switch(column) {
        case 'seq': {
          const n = (x) => {
            if (x.seq == null || x.seq === '') return null;
            const v = Number(x.seq);
            return Number.isFinite(v) ? v : null;
          };
          aVal = n(a);
          bVal = n(b);
          if (aVal == null && bVal == null) return 0;
          if (aVal == null) return 1;
          if (bVal == null) return -1;
          break;
        }
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'notes':
          aVal = (a.notes || '').toLowerCase();
          bVal = (b.notes || '').toLowerCase();
          break;
        case 'due_date': {
          const sortKey = (x) => {
            if (x.due_date == null || x.due_date === '') return '';
            const dp = getDueDatePart(x.due_date);
            if (!dp) return '';
            const tp = getDueTimePart(x.due_date);
            return `${dp} ${tp || '00:00'}`;
          };
          aVal = sortKey(a);
          bVal = sortKey(b);
          break;
        }
        case 'amount':
          aVal = parseFloat(a.amount) || 0;
          bVal = parseFloat(b.amount) || 0;
          break;
        case 'repeat':
          aVal = getRepeatLabel(a.repeat_rule);
          bVal = getRepeatLabel(b.repeat_rule);
          break;
        case 'status':
          aVal = (a.status || '').toLowerCase();
          bVal = (b.status || '').toLowerCase();
          break;
        case 'project':
          aVal = (a.project || '').toLowerCase();
          bVal = (b.project || '').toLowerCase();
          break;
        case 'category':
          aVal = (a.category || '').toLowerCase();
          bVal = (b.category || '').toLowerCase();
          break;
        case 'owner': {
          const userById = Object.fromEntries((UserObjs || []).map(u => [u._id, (getUserDisplayName(u) || '').toLowerCase()]));
          aVal = userById[a.owner] || 'unassigned';
          bVal = userById[b.owner] || 'unassigned';
          break;
        }
        case 'created_by': {
          const userById = Object.fromEntries((UserObjs || []).map(u => [u._id, (getUserDisplayName(u) || '').toLowerCase()]));
          aVal = userById[a.created_by] || '';
          bVal = userById[b.created_by] || '';
          break;
        }
        case 'priority': {
          const order = { High: 3, Medium: 2, Low: 1 };
          aVal = order[a.priority] || 0;
          bVal = order[b.priority] || 0;
          break;
        }
        case 'created_date': {
          const ts = (x) => (x.created_date != null ? dayjs(x.created_date).valueOf() : 0);
          aVal = ts(a);
          bVal = ts(b);
          break;
        }
        case 'completed_date': {
          const ts = (x) => (x.completed_date != null ? dayjs(x.completed_date).valueOf() : 0);
          aVal = ts(a);
          bVal = ts(b);
          break;
        }
        case 'schedule': {
          const ts = (x) => {
            if (x.schedule_start_time == null) return 0;
            const t = dayjs(x.schedule_start_time);
            return t.isValid() ? t.valueOf() : 0;
          };
          const ta = ts(a);
          const tb = ts(b);
          if (ta !== tb) {
            aVal = ta;
            bVal = tb;
          } else {
            aVal = getTaskScheduleStatusSortRank(getTaskScheduleStatusLabel(a));
            bVal = getTaskScheduleStatusSortRank(getTaskScheduleStatusLabel(b));
          }
          break;
        }
        default:
          return 0;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }

  const sortedTaskObjs = useMemo(() => {
    const searchLower = (searchTerm || '').trim().toLowerCase();
    const bounds = timeNav.bounds;
    const rangeStart = bounds.startInclusive ? dayjs(bounds.startInclusive) : null;
    const rangeEnd = bounds.endInclusive ? dayjs(bounds.endInclusive) : null;
    const filteredByNavigator = (labelFilteredTaskObjs || []).filter((obj) => {
      const st = (obj?.status || 'Open').toLowerCase();
      if (statusFilter === 'open' && st !== 'open') return false;
      if (statusFilter === 'closed' && st !== 'closed') return false;
      if (timeNav.view === 'all') return true;
      const due = obj?.due_date;
      const inst = getDueInstantForComparison(due);
      if (!inst || !inst.isValid()) return false;
      if (rangeStart && inst.isBefore(rangeStart)) return false;
      if (rangeEnd && inst.isAfter(rangeEnd)) return false;
      return true;
    });
    const filteredByCategory = categoryFilter.size === 0
      ? filteredByNavigator
      : filteredByNavigator.filter(obj => categoryFilter.has((obj.category || 'General').trim()));
    const projectIdParam = searchParams.get('projectId');
    const projectIdTrimmed = projectIdParam && String(projectIdParam).trim();
    const filteredByProject = projectIdTrimmed
      ? filteredByCategory.filter((obj) => String(obj.project_id || '') === projectIdTrimmed)
      : projectFilter.size === 0
        ? filteredByCategory
        : filteredByCategory.filter(obj => projectFilter.has((obj.project || '').trim()));
    const filteredByPriority = priorityFilter.size === 0
      ? filteredByProject
      : filteredByProject.filter(obj => {
        const canonical = normalizeTaskPriorityForFilter(obj.priority);
        const taskPriority = canonical || 'Not set';
        return priorityFilter.has(taskPriority);
      });
    const filteredByScheduleStatus = scheduleStatusFilter.size === 0
      ? filteredByPriority
      : filteredByPriority.filter((obj) => scheduleStatusFilter.has(getTaskScheduleStatusLabel(obj)));
    const scheduleDateTrim =
      scheduleDateFilter != null && String(scheduleDateFilter).trim() !== ''
        ? String(scheduleDateFilter).trim()
        : '';
    const filteredByScheduleDate = !scheduleDateTrim
      ? filteredByScheduleStatus
      : filteredByScheduleStatus.filter((obj) => String(obj?.schedule_calendar_date || '') === scheduleDateTrim);
    const vendorIdSet = vendorFilter;
    const noValidVendor = vendorIdSet.has('__no_valid_vendor__');
    const validVendorIds = new Set((vendorList || []).map((v) => String(v._id)));
    const filteredByVendor = vendorFilter.size === 0
      ? filteredByScheduleDate
      : noValidVendor
        ? filteredByScheduleDate.filter(obj => {
            const vid = (obj.vendor_id || '').trim();
            if (!vid) return true;
            if ((vendorList || []).length === 0) return false;
            return !validVendorIds.has(String(obj.vendor_id));
          })
        : filteredByScheduleDate.filter(obj => vendorIdSet.has((obj.vendor_id || '')));
    const ownerDrilldownRaw = searchParams.get('owner');
    const ownerDrilldownTrim =
      ownerDrilldownRaw != null && String(ownerDrilldownRaw).trim() ? String(ownerDrilldownRaw).trim() : '';
    const filteredByOwnerDrilldown = !ownerDrilldownTrim
      ? filteredByVendor
      : filteredByVendor.filter((obj) => {
          if (ownerDrilldownTrim === '__unassigned__') return !obj.owner || String(obj.owner).trim() === '';
          return String(obj.owner || '') === ownerDrilldownTrim;
        });
    const filteredByAdvanced = filterTasksByAdvancedCriteria(
      filteredByOwnerDrilldown,
      advancedFilterApplied
    );
    const userByIdForSearch = Object.fromEntries((UserObjs || []).map(u => [u._id, (getUserDisplayName(u) || '').toLowerCase()]));
    const filteredObjs = filteredByAdvanced.filter(obj => {
      if (!searchLower) return true;
      const scheduleStatusLabel = getTaskScheduleStatusLabel(obj);
      const moreBlocks = Math.max(0, (Number(obj.schedule_block_count) || 0) - 1);
      const scheduledSlotText = formatScheduledSlot(obj.schedule_start_time, obj.schedule_end_time, moreBlocks, { dateOnly: true });
      const dueStr = getDueDatePart(obj.due_date);
      const amountStr = obj.amount != null ? String(obj.amount) : '';
      const repeatStr = getRepeatLabel(obj.repeat_rule);
      const ownerDisplay = (userByIdForSearch[obj.owner] || 'unassigned');
      const createdByDisplay = (userByIdForSearch[obj.created_by] || '');
      const createdAtFmt = formatTimestampDateForTable(obj.created_date);
      const completedAtFmt = formatTimestampDateForTable(obj.completed_date);
      const labelsText = getItemLabels(obj).join(', ');
      return (
        (obj.name && obj.name.toLowerCase().includes(searchLower)) ||
        (dueStr && dueStr.toLowerCase().includes(searchLower)) ||
        (amountStr && amountStr.toLowerCase().includes(searchLower)) ||
        (repeatStr && repeatStr.toLowerCase().includes(searchLower)) ||
        (obj.status && obj.status.toLowerCase().includes(searchLower)) ||
        (scheduleStatusLabel && scheduleStatusLabel.toLowerCase().includes(searchLower)) ||
        (obj.schedule_calendar_date && String(obj.schedule_calendar_date).toLowerCase().includes(searchLower)) ||
        (scheduledSlotText && scheduledSlotText.toLowerCase().includes(searchLower)) ||
        (obj.schedule_start_time && String(obj.schedule_start_time).toLowerCase().includes(searchLower)) ||
        (obj.schedule_end_time && String(obj.schedule_end_time).toLowerCase().includes(searchLower)) ||
        (obj.project && obj.project.toLowerCase().includes(searchLower)) ||
        (obj.category && obj.category.toLowerCase().includes(searchLower)) ||
        (obj.priority && obj.priority.toLowerCase().includes(searchLower)) ||
        (obj.notes && obj.notes.toLowerCase().includes(searchLower)) ||
        (ownerDisplay && ownerDisplay.includes(searchLower)) ||
        (createdByDisplay && createdByDisplay.includes(searchLower)) ||
        (createdAtFmt !== '—' && createdAtFmt.toLowerCase().includes(searchLower)) ||
        (completedAtFmt !== '—' && completedAtFmt.toLowerCase().includes(searchLower)) ||
        (labelsText && labelsText.toLowerCase().includes(searchLower)) ||
        (obj.created_date && String(obj.created_date).toLowerCase().includes(searchLower)) ||
        (obj.completed_date && String(obj.completed_date).toLowerCase().includes(searchLower))
      );
    });

    return sortColumn
      ? sortData(filteredObjs, sortColumn, sortDirection)
      : filteredObjs;
  }, [
    labelFilteredTaskObjs,
    UserObjs,
    searchTerm,
    statusFilter,
    timeNav.view,
    timeNav.bounds,
    categoryFilter,
    projectFilter,
    priorityFilter,
    scheduleStatusFilter,
    scheduleDateFilter,
    vendorFilter,
    vendorList,
    advancedFilterApplied,
    searchParams,
    sortColumn,
    sortDirection,
  ]);

  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, sortedTaskObjs);
  const selectedVisibleCount = selectedVisibleIds.size;

  useLayoutEffect(() => {
    const prevCount = prevSelectedVisibleCountRef.current;
    prevSelectedVisibleCountRef.current = selectedVisibleCount;

    const showBulkActions =
      (viewMode === "table" || viewMode === "card") && selectedVisibleCount > 0;
    if (!showBulkActions || prevCount > 0) return;

    const bulkStartEl = taskPageHeaderBulkStartRef.current;
    if (!bulkStartEl) return;

    requestAnimationFrame(() => {
      bulkStartEl.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    });
  }, [selectedVisibleCount, viewMode]);

  const {
    pageItems: paginatedTaskObjs,
    pageSize,
    setPageSize,
    paginationBarProps,
    getSelectAllCheckboxProps,
  } = useListPagination(sortedTaskObjs, [
    searchTerm,
    statusFilter,
    timeNav.view,
    timeNav.bounds?.startInclusive,
    timeNav.bounds?.endInclusive,
    categoryFilter,
    projectFilter,
    priorityFilter,
    scheduleStatusFilter,
    scheduleDateFilter,
    vendorFilter,
    advancedFilterApplied,
    searchParams.get('projectId'),
    searchParams.get('owner'),
    activeLabel,
    sortColumn,
    sortDirection,
    CurrentObjs,
  ]);

  const collectTaskPageViewState = useCallback(
    () =>
      collectTaskListPagePreferenceState({
        searchTerm,
        statusFilter,
        viewMode,
        navView: timeNav.view,
        navDate: timeNav.focusDateYmd,
        advancedFilter: advancedFilterApplied,
        categoryFilter,
        projectFilter,
        priorityFilter,
        scheduleStatusFilter,
        vendorFilter,
        scheduleDateFilter,
        sortColumn,
        sortDirection,
        pageSize,
      }),
    [
      searchTerm,
      statusFilter,
      viewMode,
      timeNav.view,
      timeNav.focusDateYmd,
      advancedFilterApplied,
      categoryFilter,
      projectFilter,
      priorityFilter,
      scheduleStatusFilter,
      vendorFilter,
      scheduleDateFilter,
      sortColumn,
      sortDirection,
      pageSize,
    ]
  );

  const applyTaskPageViewState = useCallback(
    (state) => {
      applyTaskListPagePreferenceState(state, {
        setSearchTerm,
        setStatusFilterSynced,
        setViewMode,
        setNavView: timeNav.setView,
        setNavDate: timeNav.setFocusDateYmd,
        setAdvancedFilterApplied,
        setCategoryFilter,
        setProjectFilter,
        setPriorityFilter,
        setScheduleStatusFilter,
        setVendorFilter,
        setScheduleDateFilter,
        setSortColumn,
        setSortDirection,
        setPageSize,
      });
    },
    [
      setStatusFilterSynced,
      timeNav.setView,
      timeNav.setFocusDateYmd,
      setAdvancedFilterApplied,
      setPageSize,
    ]
  );

  const taskPageViewPreferenceHookConfig = useMemo(
    () => ({
      pageKey: PAGE_VIEW_PREFERENCE_KEYS.taskList,
      accountId: account_id,
      userId: currentUserId,
      enabled: Boolean(account_id && currentUserId),
      shouldSkipLoad: shouldSkipTaskPagePreferenceLoad,
      collectState: collectTaskPageViewState,
      applyState: applyTaskPageViewState,
    }),
    [
      account_id,
      currentUserId,
      shouldSkipTaskPagePreferenceLoad,
      collectTaskPageViewState,
      applyTaskPageViewState,
    ]
  );

  const taskPageViewPreferences = usePageViewPreferences(taskPageViewPreferenceHookConfig);

  const taskPageViewPreferenceProps = useMemo(
    () => ({
      ...taskPageViewPreferenceHookConfig,
      preferencesControl: taskPageViewPreferences,
      onAfterClearLocalState: clearTaskPageFiltersAfterDefaultCleared,
      onClearConfirm: confirmClearTaskDefaultView,
      onError: (message) => showAlertDialog("Default view", message),
    }),
    [
      taskPageViewPreferenceHookConfig,
      taskPageViewPreferences,
      clearTaskPageFiltersAfterDefaultCleared,
      confirmClearTaskDefaultView,
      showAlertDialog,
    ]
  );

  const selectedTaskItems = useMemo(
    () => sortedTaskObjs.filter((item) => selectedIds.has(item._id)),
    [sortedTaskObjs, selectedIds]
  );

  const sharedBulkLabels = useMemo(
    () => getSharedLabelsAmongItems(selectedTaskItems),
    [selectedTaskItems]
  );

  const activeProjectId = useMemo(() => {
    const raw = searchParams.get('projectId');
    return raw != null && String(raw).trim() ? String(raw).trim() : '';
  }, [searchParams]);

  const activeProject = useMemo(() => {
    if (!activeProjectId) return null;
    const p = (ProjectObjs || []).find((x) => String(x?._id) === activeProjectId);
    return p || { _id: activeProjectId, name: 'Unknown project' };
  }, [ProjectObjs, activeProjectId]);

  const activeProjectVendorLabel = useMemo(() => {
    if (!activeProject) return '';
    const vid = activeProject?.vendor_id != null ? String(activeProject.vendor_id).trim() : '';
    if (!vid) return '—';
    const v = (vendorList || []).find((x) => String(x?._id) === vid);
    if (!v) return '—';
    return (v?.name || 'Vendor') + (v?.type ? ` (${v.type})` : '');
  }, [activeProject, vendorList]);

  const activeProjectAssetLabel = useMemo(() => {
    if (!activeProject) return '';
    const aid = activeProject?.asset_id != null ? String(activeProject.asset_id).trim() : '';
    if (!aid) return '—';
    const a = (assetList || []).find((x) => String(x?._id) === aid);
    return a ? getAssetLabel(a) : '—';
  }, [activeProject, assetList]);

  const activeProjectProgress = useMemo(() => {
    if (!activeProjectId) return { total: 0, closed: 0 };
    const list = Array.isArray(CurrentObjs) ? CurrentObjs : [];
    const projTasks = list.filter((t) => String(t?.project_id || '') === activeProjectId);
    const total = projTasks.length;
    const closed = projTasks.filter((t) => (t?.status && String(t.status).trim() === 'Closed')).length;
    return { total, closed };
  }, [CurrentObjs, activeProjectId]);

  const handleRequestCloseProject = useCallback(async () => {
    if (!activeProject?._id) return;
    const pid = activeProject._id;
    let confirmMessage = 'Close this project?\n\nNo related sub-tasks or time blocks to close.';
    try {
      const previewRes = await apiFetch(serverUrl + '/previewCloseProject', {
        method: 'POST',
        body: JSON.stringify({ account_id, _id: pid }),
      });
      if (previewRes.ok) {
        const p = await previewRes.json();
        const taskLine = `Tasks: ${Number(p.tasksWillClose || 0)} will close (${Number(p.tasksAlreadyClosed || 0)} already closed of ${Number(p.tasksTotal || 0)} total)`;
        const cascadePreview = formatChildCascadeLines(
          {
            subTasksMatched: p.subTasksMatched,
            subTasksModified: p.subTasksMatched,
            timeBlocksMatched: p.timeBlocksMatched,
            timeBlocksModified: p.timeBlocksMatched,
          },
          { future: true, futureContext: 'close' }
        );
        confirmMessage = ['Close this project?', '', taskLine, '', ...cascadePreview].join('\n');
      }
    } catch {
      confirmMessage = 'Close this project?';
    }
    setConfirmDialog({
      open: true,
      title: 'Close project',
      message: confirmMessage,
      onConfirm: async () => {
        try {
          const response = await apiFetch(serverUrl + '/closeProject', {
            method: 'POST',
            body: JSON.stringify({
              account_id,
              _id: pid,
              user_id: userInfo?._id || '',
              viewer_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
          });
          if (response.ok) {
            getProjectList();
            getDocument();
          } else {
            const ct = (response.headers.get('content-type') || '').toLowerCase();
            const body = ct.includes('application/json') ? await response.json().catch(() => null) : await response.text();
            const msg = typeof body === 'string' ? body : (body && body.message ? body.message : null);
            showAlertDialog('Close project failed', msg || 'Failed to close project');
          }
        } catch (error) {
          setBannerError(getMessageForFetchError(error, 'Failed to close project'));
        }
      },
    });
  }, [activeProject, account_id, serverUrl, userInfo, getProjectList, getDocument, showAlertDialog]);

  if (authLoading) return <div>Loading…</div>;
  if (!account_id) return <div style={{ padding: '1rem' }}>Account information is missing. Please log in again.</div>;

  const canEditOwner = hasRole('Admin') || hasRole('Super Admin');

  return (
    <div className="page-container page-container--section-tabs">
      <SectionTabsPageHeader subtitle="View and manage your tasks.">
        <TaskSectionTabs activeTab={TASK_TAB_MANAGEMENT} />
      </SectionTabsPageHeader>
      <div className="page-header task-page-header-actions">
        {!activeProjectId ? (
          <HorizontalScrollHint
            className="h-scroll-hint--surface"
            scrollerClassName="task-page-header-actions__scroll"
            role="toolbar"
            aria-label="Task actions"
          >
            {(viewMode === 'table' || viewMode === 'card') && selectedVisibleCount > 0 ? (
              <>
                <button
                  ref={taskPageHeaderBulkStartRef}
                  type="button"
                  onClick={openBulkLabels}
                  disabled={labelUpdating || deleting || closing || bulkUpdating}
                  className="btn btn-sm btn-muted toolbar-control"
                >
                  {labelUpdating && bulkLabelsOpen ? 'Updating…' : `Labels (${selectedVisibleCount})`}
                </button>
                <button
                  type="button"
                  onClick={openBulkUpdate}
                  disabled={bulkUpdating || labelUpdating}
                  className="btn btn-sm btn-muted toolbar-control"
                >
                  Update ({selectedVisibleCount})
                </button>
                <button
                  type="button"
                  onClick={closeSelected}
                  disabled={closing || labelUpdating}
                  className="btn btn-sm btn-muted toolbar-control"
                >
                  Close ({selectedVisibleCount})
                </button>
                <button
                  type="button"
                  onClick={deleteSelected}
                  disabled={deleting || labelUpdating}
                  className="btn btn-sm btn-danger toolbar-control"
                >
                  Delete ({selectedVisibleCount})
                </button>
                <span className="toolbar-divider task-page-header-actions__divider" aria-hidden="true" />
              </>
            ) : null}
            <button
              type="button"
              className={`btn btn-sm ${importPanelExpanded ? 'btn-primary-outline active' : 'btn-primary'}`}
              onClick={() => setImportPanelExpanded((expanded) => !expanded)}
              title="Import tasks from CSV"
            >
              {importPanelExpanded ? '− Import CSV' : '+ Import CSV'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                setImportPanelExpanded(false);
                setSmartAddModalOpen(true);
              }}
              title="Add Task (NLP)"
            >
              <IconLightning size={16} style={{ marginRight: 4 }} />
              Add Task (NLP)
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setImportPanelExpanded(false);
                setAddTaskModalOpen(true);
              }}
              title="Add Task"
            >
              <IconDocPlus size={16} style={{ marginRight: 4 }} />
              Add Task
            </button>
          </HorizontalScrollHint>
        ) : null}
      </div>

      {activeProjectId && (
        <CollapsibleProjectHeader
          project={activeProject}
          vendorLabel={activeProjectVendorLabel}
          assetLabel={activeProjectAssetLabel}
          vendorList={vendorList}
          assetList={assetList}
          accountId={account_id}
          serverUrl={serverUrl}
          onAfterSave={() => { getProjectList(); getDocument(); }}
          onNotify={(title, message) => showAlertDialog(title, message)}
          onRequestCloseProject={handleRequestCloseProject}
          totalTasks={activeProjectProgress.total}
          closedTasks={activeProjectProgress.closed}
          storageKey={`task.projectHeaderCollapsed:${activeProjectId}`}
        />
      )}

      <ErrorBanner message={loadError || bannerError} onDismiss={() => { setLoadError(''); setBannerError(''); }} />
      {!activeProjectId && importPanelExpanded ? (
        <ImportTasks onImportComplete={getDocument} />
      ) : null}
      <ColumnCustomizerModal
        open={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        allColumns={TASK_TABLE_COLUMNS}
        visibleIds={visibleTaskColumns}
        onChangeVisibleIds={setVisibleTaskColumns}
        saving={columnPrefsSaving}
        onReset={async () => {
          const defaultIds = [...TASK_TABLE_DEFAULT_VISIBLE_COLUMNS];
          setVisibleTaskColumns(defaultIds);
          if (!account_id || !currentUserId) {
            setColumnModalOpen(false);
            return;
          }
          try {
            setColumnPrefsSaving(true);
            await saveTablePreferences(account_id, currentUserId, TASK_TABLE_PREFERENCE_KEY, defaultIds);
          } catch (err) {
            setConfirmDialog({ open: true, title: 'Task columns', message: err && err.message ? err.message : 'Failed to reset column preferences', onConfirm: null, mode: 'alert' });
          } finally {
            setColumnPrefsSaving(false);
          }
          setColumnModalOpen(false);
        }}
        onSave={async (ids) => {
          const normalizedIds = normalizeTaskTableColumnIds(ids);
          if (!account_id || !currentUserId) {
            setVisibleTaskColumns(normalizedIds);
            setColumnModalOpen(false);
            return;
          }
          try {
            setColumnPrefsSaving(true);
            await saveTablePreferences(account_id, currentUserId, TASK_TABLE_PREFERENCE_KEY, normalizedIds);
            setVisibleTaskColumns(normalizedIds);
            setColumnModalOpen(false);
          } catch (err) {
            setConfirmDialog({ open: true, title: 'Task columns', message: err && err.message ? err.message : 'Failed to save column preferences', onConfirm: null, mode: 'alert' });
          } finally {
            setColumnPrefsSaving(false);
          }
        }}
      />

      <BulkUpdateTasksModal
        open={bulkUpdateOpen}
        onClose={() => {
          if (bulkUpdating) return;
          setBulkUpdateOpen(false);
        }}
        selectedCount={selectedVisibleCount}
        projects={ProjectObjs || []}
        loading={bulkUpdating}
        onApply={applyBulkUpdate}
      />

      <EntityLabelsModal
        open={bulkLabelsOpen}
        onClose={() => {
          if (labelUpdating) return;
          setBulkLabelsOpen(false);
        }}
        labelOptions={labelOptions}
        selectedLabels={sharedBulkLabels}
        onApplyLabels={handleBulkApplyLabels}
        onAddLabel={handleAddLabel}
        onRenameLabel={handleRenameLabel}
        onRemoveLabel={handleRemoveLabel}
        setConfirmDialog={setConfirmDialog}
        saving={labelUpdating}
        subtitle={`Select labels to apply to ${selectedVisibleCount} selected task${selectedVisibleCount === 1 ? '' : 's'}. Rename or delete from the same row.`}
      />


      <ConfirmationModal
        open={confirmDialog.open}
        title={confirmDialog.title || "Confirm"}
        message={confirmDialog.message}
        mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
        confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
        onConfirm={() => {
          const fn = confirmDialog.onConfirm;
          setConfirmDialog({ open: false, title: "", message: "", onConfirm: null, mode: undefined });
          if (fn) fn();
        }}
        onCancel={() =>
          setConfirmDialog({ open: false, title: "", message: "", onConfirm: null, mode: undefined })
        }
      />

      <ConfirmationModal
        open={tbAutoScheduleDialog.open}
        title="Auto schedule task"
        mode="confirm"
        confirmLabel="Schedule"
        cancelLabel="Cancel"
        message={
          tbAutoScheduleDialog.task && tbAutoScheduleDialog.start && tbAutoScheduleDialog.end
            ? (() => {
                const start = tbAutoScheduleDialog.start;
                const end = tbAutoScheduleDialog.end;
                const startLabel = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                const endLabel = end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                const dateLabel = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                const [wsH = '0', wsM = '0'] = String(workingHoursStart || '').split(':');
                const [weH = '0', weM = '0'] = String(workingHoursEnd || '').split(':');
                const whStart = (() => {
                  const d = new Date(start.getTime());
                  d.setHours(parseInt(wsH, 10) || 0, parseInt(wsM, 10) || 0, 0, 0);
                  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                })();
                const whEnd = (() => {
                  const d = new Date(start.getTime());
                  d.setHours(parseInt(weH, 10) || 0, parseInt(weM, 10) || 0, 0, 0);
                  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                })();
                return (
                  `Schedule "${tbAutoScheduleDialog.task.name}" at ${startLabel} – ${endLabel} on ${dateLabel}?` +
                  `\n\nWorking hours: ${whStart} – ${whEnd}`
                );
              })()
            : 'Schedule this task in the earliest open 30-minute slot?'
        }
        onCancel={() => setTbAutoScheduleDialog({ open: false, task: null, start: null, end: null })}
        onConfirm={async () => {
          const { task, start, end } = tbAutoScheduleDialog;
          if (!account_id || !currentUserId || !task?._id || !start || !end) return;
          const startIso = start.toISOString();
          await createTimeBlock({
            account_id,
            task_id: task._id,
            user_id: currentUserId,
            start_time: startIso,
            end_time: end.toISOString(),
            status: 'scheduled',
          });
          setTbAutoScheduleDialog({ open: false, task: null, start: null, end: null });
          await getDocument();
          navigate('/TimeBlocking', { state: { focusSlotStart: startIso } });
        }}
      />

      <ConfirmationModal
        open={tbAutoScheduleAlert.open}
        title={tbAutoScheduleAlert.title || 'Info'}
        mode="alert"
        message={tbAutoScheduleAlert.message || ''}
        onCancel={() => setTbAutoScheduleAlert({ open: false, title: '', message: '' })}
        onConfirm={() => setTbAutoScheduleAlert({ open: false, title: '', message: '' })}
      />

      <TaskAddModal
        open={addTaskModalOpen}
        dueTimeOptions={taskDueTimeOptions}
        focusDueTime={workingHoursStart || undefined}
        onCancel={() => {
          setAddTaskModalOpen(false);
          const params = new URLSearchParams(searchParams);
          params.delete('add');
          setSearchParams(params);
        }}
        onSave={async (formValues) => {
          const payload = {
            account_id,
            name: formValues.name,
            due_date: formValues.due_date,
            priority: formValues.priority || '',
            amount: formValues.amount,
            repeat_rule: formValues.repeat_rule,
            ...(othersProjectId && String(othersProjectId).trim() !== '' ? { project_id: othersProjectId } : {}),
            ...(labelsForNewItem ? { labels: labelsForNewItem } : {}),
            created_by: currentUserId,
          };
          try {
            await apiFetch(serverUrl + '/createTask', {
              method: 'POST',
              body: JSON.stringify(payload),
            });
            await getDocument();
            setAddTaskModalOpen(false);
            const params = new URLSearchParams(searchParams);
            params.delete('add');
            setSearchParams(params);
          } catch (err) {
            setBannerError(getMessageForFetchError(err));
            throw err;
          }
        }}
      />

      <SmartAddModal
        open={smartAddModalOpen}
        accountId={account_id}
        createdBy={currentUserId}
        othersProjectId={othersProjectId}
        labelsForNewItem={labelsForNewItem}
        onClose={() => {
          setSmartAddModalOpen(false);
          const params = new URLSearchParams(searchParams);
          params.delete('smart');
          setSearchParams(params);
        }}
        onCreated={async (_autoSchedule, createdCount) => {
          const n = Number(createdCount) || 0;
          setConfirmDialog({
            open: true,
            title: "Add Task (NLP)",
            message: `Done. ${n} task(s) created.`,
            mode: "alert",
            onConfirm: null,
          });
          await getDocument();
        }}
      />

      {/* Task Detail view (TaskView) - opens when task name is clicked */}
      {detailTaskId && detailTask && (() => {
        const userById = Object.fromEntries((UserObjs || []).map(u => [u._id, getUserDisplayName(u)]));
        const createSubTask = () => {
          const n = (subTaskForm.name || '').trim();
          if (!n) { setBannerError('Sub-task name is required'); return; }
          const subDue = subTaskForm.due_date || dayjs().format('YYYY-MM-DD');
          const subDateCheck = validateDate(subDue);
          if (!subDateCheck.valid) { setBannerError(subDateCheck.message); return; }
          apiFetch(serverUrl + '/createSubTask', {
            method: 'POST',
            body: JSON.stringify({
              account_id,
              task_id: detailTaskId,
              name: n,
              due_date: getDueDateForApi(subDue, ''),
              priority: subTaskForm.priority || 'Medium',
            }),
          })
            .then(async (r) => {
              const text = await r.text();
              if (!r.ok) {
                setBannerError(text || 'Failed to create sub-task');
                return;
              }
              getSubTasks(detailTaskId);
              setSubTaskForm({ name: '', due_date: dayjs().format('YYYY-MM-DD'), priority: 'Medium' });
              setShowSubTaskForm(false);
            })
            .catch(err => setBannerError(getMessageForFetchError(err)));
        };
        const createBulkSubTasks = async () => {
          const names = bulkSubTaskInput
            .split(/[,\n\r]+/)
            .map((s) => s.trim())
            .filter(Boolean);
          if (names.length === 0) {
            setBannerError('Enter at least one sub-task name, separated by commas or line breaks.');
            return;
          }
          const subDue = dayjs().format('YYYY-MM-DD');
          const subDateCheck = validateDate(subDue);
          if (!subDateCheck.valid) { setBannerError(subDateCheck.message); return; }
          setBulkSubTaskSubmitting(true);
          try {
            for (const n of names) {
              const r = await apiFetch(serverUrl + '/createSubTask', {
                method: 'POST',
                body: JSON.stringify({
                  account_id,
                  task_id: detailTaskId,
                  name: n,
                  due_date: getDueDateForApi(subDue, ''),
                  priority: 'Medium',
                }),
              });
              const text = await r.text();
              if (!r.ok) {
                setBannerError(text || 'Failed to create sub-task');
                return;
              }
            }
            getSubTasks(detailTaskId);
            setBulkSubTaskInput('');
            setShowBulkSubTaskForm(false);
          } catch (err) {
            setBannerError(getMessageForFetchError(err));
          } finally {
            setBulkSubTaskSubmitting(false);
          }
        };
        const updateSubTask = (subId, payload) => {
          apiFetch(serverUrl + '/updateSubTask', {
            method: 'POST',
            body: JSON.stringify({ account_id, _id: subId, ...payload }),
          })
            .then(async (r) => {
              const text = await r.text();
              if (!r.ok) {
                setBannerError(text || 'Failed to update sub-task');
                return;
              }
              getSubTasks(detailTaskId);
              setEditingSubTaskId(null);
              setSubTaskEditDraft(null);
            })
            .catch(err => setBannerError(getMessageForFetchError(err)));
        };
        const deleteSubTask = (subId) => {
          apiFetch(serverUrl + '/deleteSubTask', {
            method: 'POST',
            body: JSON.stringify({ account_id, _id: subId }),
          })
            .then(async (r) => {
              const text = await r.text();
              if (!r.ok) {
                setBannerError(text || 'Failed to delete sub-task');
                return;
              }
              getSubTasks(detailTaskId);
            })
            .catch(err => setBannerError(getMessageForFetchError(err)));
        };
        const closeSubTask = (subId) => {
          apiFetch(serverUrl + '/closeSubTask', {
            method: 'POST',
            body: JSON.stringify({ account_id, _id: subId }),
          })
            .then(async (r) => {
              const text = await r.text();
              if (!r.ok) {
                setBannerError(text || 'Failed to complete sub-task');
                return;
              }
              getSubTasks(detailTaskId);
            })
            .catch(err => setBannerError(getMessageForFetchError(err)));
        };
        const reopenSubTask = (subId) => {
          apiFetch(serverUrl + '/reopenSubTask', {
            method: 'POST',
            body: JSON.stringify({ account_id, _id: subId }),
          })
            .then(async (r) => {
              const text = await r.text();
              if (!r.ok) {
                setBannerError(text || 'Failed to reopen sub-task');
                return;
              }
              getSubTasks(detailTaskId);
            })
            .catch(err => setBannerError(getMessageForFetchError(err)));
        };
        const startEditSubTask = (st) => {
          setEditingSubTaskId(st._id);
          setSubTaskEditDraft({
            name: st.name,
            due_date: getDueDatePart(st.due_date) || '',
            priority: st.priority || '',
          });
        };
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
            <div role="button" tabIndex={0} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.3)' }} onClick={() => setDetailTaskId(null)} onKeyDown={(e) => e.key === 'Escape' && setDetailTaskId(null)} aria-label="Close" />
            <div className="panel" style={{ position: 'relative', width: '100%', maxWidth: 720, minWidth: 0, margin: 0, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', overflow: 'auto', zIndex: 1001, maxHeight: '100%' }}>
              <div className="panel-row" style={{ justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', padding: '12px 16px', marginBottom: '12px' }}>
                <h3 className="panel-title" style={{ margin: 0, fontSize: '1.1rem' }}>Task details</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <DetailSaveStatus isSaving={detailSaving} hasChanges={detailHasChanges} />
                  {detailTask.status === 'Closed' ? (
                    <button
                      type="button"
                      onClick={() => reopenOneTask(detailTaskId)}
                      className="btn-link"
                      style={iconBtnStyle}
                      title="Reopen"
                      disabled={taskStatusActionIds.has(String(detailTaskId))}
                      aria-disabled={taskStatusActionIds.has(String(detailTaskId)) ? 'true' : 'false'}
                      aria-label="Reopen task"
                    >
                      <IconReopen size={18} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => closeOneTask(detailTask)}
                      className="btn-link-muted"
                      style={iconBtnStyle}
                      title="Mark Complete"
                      disabled={taskStatusActionIds.has(String(detailTaskId))}
                      aria-disabled={taskStatusActionIds.has(String(detailTaskId)) ? 'true' : 'false'}
                      aria-label="Mark task complete"
                    >
                      <IconClose size={18} />
                    </button>
                  )}
                  <button type="button" className="btn-link btn-link-muted" onClick={() => setDetailTaskId(null)} style={{ fontSize: '18px', lineHeight: 1, padding: '4px 8px' }} aria-label="Close">×</button>
                </div>
              </div>
              <div style={{ padding: '0 16px 16px 16px', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                <div style={{ marginTop: 0 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', columnGap: '16px', rowGap: '12px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Name</label>
                        {detailActiveField === 'name' ? (
                          <input autoFocus type="text" value={detailName} onChange={(e) => setDetailName(e.target.value)} onBlur={handleDetailFieldBlur} onKeyDown={handleDetailFieldCommitKeyDown} placeholder="Name" className="input input-full" />
                        ) : (
                          <button type="button" onClick={() => setDetailActiveField('name')} className="btn-link" style={{ textAlign: 'left', padding: '4px 0', color: '#212529', textDecoration: 'none', fontWeight: 500 }}>{detailName || '—'}</button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Due</label>
                        {detailActiveField === 'due' ? (
                          <DateTimeFieldGroup
                            layout="inline"
                            date={detailDueDate}
                            onDateChange={setDetailDueDate}
                            time={detailDueTime}
                            onTimeChange={setDetailDueTime}
                            timeOptions={taskDueTimeOptions}
                            onBlur={handleDetailFieldBlur}
                            onKeyDown={handleDetailFieldCommitKeyDown}
                            autoFocus
                            dateAriaLabel="Due date"
                            timeAriaLabel="Due time"
                          />
                        ) : (
                          <button type="button" onClick={() => setDetailActiveField('due')} className="btn-link" style={{ textAlign: 'left', padding: '4px 0', color: '#212529', textDecoration: 'none', fontWeight: 500 }}>{formatDueDate(getDueDateForApi(detailDueDate, detailDueTime && detailDueTime.trim() ? detailDueTime.trim() : '00:00')) || '—'}</button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Amount($)</label>
                        {detailActiveField === 'amount' ? (
                          <input autoFocus type="text" value={detailAmount} onChange={(e) => setDetailAmount(e.target.value === '' ? 0 : (parseFloat(e.target.value) || 0))} onBlur={handleDetailFieldBlur} onKeyDown={handleDetailFieldCommitKeyDown} placeholder="0" className="input input-full input-number-no-spin" />
                        ) : (
                          <button type="button" onClick={() => setDetailActiveField('amount')} className="btn-link" style={{ textAlign: 'left', padding: '4px 0', color: '#212529', textDecoration: 'none', fontWeight: 500 }}>{Number(detailAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Repeat</label>
                        {detailActiveField === 'repeat' ? (
                          <RepeatPatternPicker
                            anchorDate={detailDueDate}
                            value={detailRepeatRule}
                            onChange={setDetailRepeatRule}
                            onBlur={handleDetailFieldBlur}
                            autoFocus
                            dropdownPortal
                          />
                        ) : (
                          <button type="button" onClick={() => setDetailActiveField('repeat')} className="btn-link" style={{ textAlign: 'left', padding: '4px 0', color: '#212529', textDecoration: 'none', fontWeight: 500 }}>{getRepeatLabel(detailRepeatRule)}</button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Priority</label>
                        {detailActiveField === 'priority' ? (
                          <select autoFocus value={detailPriority} onChange={(e) => setDetailPriority(e.target.value)} onBlur={handleDetailFieldBlur} onKeyDown={handleDetailFieldCommitKeyDown} className="input input-full">
                            <option value="">—</option>
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                          </select>
                        ) : (
                          <button type="button" onClick={() => setDetailActiveField('priority')} className="btn-link" style={{ textAlign: 'left', padding: '4px 0', color: '#212529', textDecoration: 'none', fontWeight: 500 }}>{detailPriority || '—'}</button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Owner</label>
                        {detailActiveField === 'owner' ? (
                          <select autoFocus value={detailOwner} onChange={(e) => setDetailOwner(e.target.value)} onBlur={handleDetailFieldBlur} onKeyDown={handleDetailFieldCommitKeyDown} disabled={!canEditOwner} className="input input-full" style={{ opacity: canEditOwner ? 1 : 0.8 }} title={!canEditOwner ? 'Only Admin and Super Admin can change owner' : undefined}>
                            <option value="">Unassigned</option>
                            {UserObjs.map((u) => (
                              <option key={u._id} value={u._id}>{getUserDisplayName(u) || u._id}</option>
                            ))}
                          </select>
                        ) : (
                          <button type="button" onClick={() => canEditOwner && setDetailActiveField('owner')} className="btn-link" disabled={!canEditOwner} style={{ textAlign: 'left', padding: '4px 0', color: canEditOwner ? '#212529' : '#6c757d', textDecoration: 'none', fontWeight: 500, cursor: canEditOwner ? 'pointer' : 'default' }} title={!canEditOwner ? 'Only Admin and Super Admin can change owner' : undefined}>{userById[detailOwner] || 'Unassigned'}</button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Category</label>
                        <div style={{ minHeight: 30, display: 'flex', alignItems: 'center', color: '#212529', fontWeight: 500 }}>{detailTask.category || 'General'}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Created</label>
                        <div style={{ minHeight: 30, display: 'flex', alignItems: 'center', color: '#212529', fontWeight: 500 }}>{formatTimestampForTable(detailTask.created_date)}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Completed</label>
                        <div style={{ minHeight: 30, display: 'flex', alignItems: 'center', color: '#212529', fontWeight: 500 }}>{formatTimestampForTable(detailTask.completed_date)}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Project</label>
                        {detailActiveField === 'project' ? (
                          <SearchableSelect
                            value={detailProjectId || ''}
                            onChange={(v) => setDetailProjectId(String(v || ''))}
                            options={(ProjectObjs || []).map((p) => ({ value: String(p._id), label: String(p?.name || 'Project') }))}
                            placeholder="Select…"
                            searchPlaceholder="Search project…"
                            allowEmpty={true}
                            emptyLabel="Select…"
                            maxVisibleItems={4}
                            dropdownPortal={true}
                            title="Project"
                            autoFocus
                            onBlur={handleDetailFieldBlur}
                            onKeyDown={handleDetailFieldCommitKeyDown}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDetailActiveField('project')}
                            className="btn-link"
                            style={{ textAlign: 'left', padding: '4px 0', color: '#212529', textDecoration: 'none', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}
                            title={(ProjectObjs || []).find((p) => String(p._id) === String(detailProjectId))?.name || detailTask?.project || '—'}
                          >
                            {(ProjectObjs || []).find((p) => String(p._id) === String(detailProjectId))?.name || detailTask?.project || '—'}
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Vendor</label>
                        {detailActiveField === 'vendor' ? (
                          <SearchableSelect
                            value={detailVendorId || ''}
                            onChange={(v) => setDetailVendorId(v)}
                            options={(vendorList || []).map((v) => ({
                              value: String(v._id),
                              label: `${v.name || 'Vendor'}${v.type ? ` (${v.type})` : ''}`,
                              keywords: [v.name, v.type, v.phone, v.email].filter(Boolean).join(' '),
                            }))}
                            placeholder="Select vendor…"
                            searchPlaceholder="Search vendor…"
                            allowEmpty={true}
                            emptyLabel="— None"
                            maxVisibleItems={4}
                            dropdownPortal={true}
                            title="Vendor"
                            autoFocus
                            onBlur={handleDetailFieldBlur}
                            onKeyDown={handleDetailFieldCommitKeyDown}
                          />
                        ) : (
                          <button type="button" onClick={() => { setDetailActiveField('vendor'); }} className="btn-link" style={{ textAlign: 'left', padding: '4px 0', color: '#212529', textDecoration: 'none', fontWeight: 500 }}>
                            {detailVendorId ? `${vendorList.find(v => String(v._id) === String(detailVendorId))?.name || 'Unknown vendor'}${vendorList.find(v => String(v._id) === String(detailVendorId))?.type ? ` (${vendorList.find(v => String(v._id) === String(detailVendorId))?.type})` : ''}` : '— None'}
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left', visibility: 'hidden' }} aria-hidden="true">
                          {'\u00a0'}
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', minHeight: 30 }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            title={String(detailProjectId || '').trim() ? 'Show tasks for this project' : 'No project assigned'}
                            aria-label="Go to project"
                            disabled={!String(detailProjectId || '').trim()}
                            onClick={() => applyProjectIdUrlFilter(detailProjectId)}
                          >
                            Go to project
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: '1 / span 3' }}>
                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#6c757d', textAlign: 'left' }}>Notes</label>
                        {detailActiveField === 'notes' ? (
                          <textarea
                            autoFocus
                            value={detailNotes}
                            onChange={(e) => setDetailNotes(e.target.value)}
                            onBlur={handleDetailFieldBlur}
                            onKeyDown={handleDetailFieldCommitKeyDown}
                            placeholder="Notes"
                            rows={3}
                            className="input input-full"
                          />
                        ) : (
                          <LinkifiedEditableRead
                            text={detailNotes}
                            onBeginEdit={() => setDetailActiveField('notes')}
                            style={{ textAlign: 'left', padding: '4px 0', color: '#212529', textDecoration: 'none', minHeight: 48, whiteSpace: 'pre-wrap' }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Save/Cancel handled in header when editing */}
                </div>

                <>
                  <>
                    <h4 style={{ margin: '24px 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#212529' }}>
                      Sub-tasks <span style={{ fontWeight: 500, color: '#6c757d' }}>({subTasks.length})</span>
                    </h4>
                    {!showSubTaskForm && !showBulkSubTaskForm ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        <button
                          type="button"
                          onClick={() => { setShowSubTaskForm(true); setShowBulkSubTaskForm(false); }}
                          style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '4px', border: '1px solid #0d6efd', backgroundColor: '#fff', color: '#0d6efd', cursor: 'pointer' }}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowBulkSubTaskForm(true); setShowSubTaskForm(false); }}
                          style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '4px', border: '1px solid #0d6efd', backgroundColor: '#fff', color: '#0d6efd', cursor: 'pointer' }}
                        >
                          Bulk Add
                        </button>
                      </div>
                    ) : null}
                    {showBulkSubTaskForm ? (
                      <div style={{ marginBottom: '12px', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                        <textarea
                          value={bulkSubTaskInput}
                          onChange={(e) => setBulkSubTaskInput(e.target.value)}
                          placeholder={'One per line, or: Task 1, Task 2, Task 3'}
                          rows={3}
                          disabled={bulkSubTaskSubmitting}
                          className="input input-full"
                          style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '8px', fontSize: '13px', resize: 'vertical' }}
                        />
                        <p style={{ fontSize: '12px', color: '#6c757d', margin: '6px 0 8px' }}>
                          Separate tasks with commas or line breaks (e.g. Task 1, Task 2, or one name per line).
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => { void createBulkSubTasks(); }}
                            disabled={bulkSubTaskSubmitting}
                            style={{ padding: '6px 12px', fontSize: '13px', border: '1px solid #0d6efd', borderRadius: '4px', backgroundColor: '#0d6efd', color: '#fff', cursor: bulkSubTaskSubmitting ? 'not-allowed' : 'pointer', opacity: bulkSubTaskSubmitting ? 0.7 : 1 }}
                          >
                            {bulkSubTaskSubmitting ? 'Adding…' : 'Add sub-tasks'}
                          </button>
                          <button
                            type="button"
                            disabled={bulkSubTaskSubmitting}
                            onClick={() => { setShowBulkSubTaskForm(false); setBulkSubTaskInput(''); }}
                            style={{ padding: '6px 10px', fontSize: '13px', border: '1px solid #6c757d', borderRadius: '4px', backgroundColor: '#fff', color: '#6c757d', cursor: bulkSubTaskSubmitting ? 'not-allowed' : 'pointer' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : null}
                    {showSubTaskForm ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px', minWidth: 0 }}>
                        <input type="text" value={subTaskForm.name} onChange={(e) => setSubTaskForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" style={{ flex: '1 1 120px', minWidth: 0, maxWidth: '100%', padding: '6px 8px', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px', boxSizing: 'border-box' }} />
                        <input type="date" value={subTaskForm.due_date} onChange={(e) => setSubTaskForm(f => ({ ...f, due_date: e.target.value }))} style={{ flex: '0 0 auto', padding: '6px 8px', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px' }} />
                        <select value={subTaskForm.priority} onChange={(e) => setSubTaskForm(f => ({ ...f, priority: e.target.value }))} style={{ flex: '0 0 auto', padding: '6px 8px', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                          <option value="">—</option>
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                        </select>
                        <button type="button" onClick={createSubTask} style={{ flexShrink: 0, padding: '6px 12px', fontSize: '13px', border: '1px solid #0d6efd', borderRadius: '4px', backgroundColor: '#0d6efd', color: '#fff', cursor: 'pointer' }}>Add</button>
                        <button
                          type="button"
                          onClick={() => { setShowSubTaskForm(false); setSubTaskForm({ name: '', due_date: dayjs().format('YYYY-MM-DD'), priority: 'Medium' }); }}
                          style={{ flexShrink: 0, padding: '6px 10px', fontSize: '13px', border: '1px solid #6c757d', borderRadius: '4px', backgroundColor: '#fff', color: '#6c757d', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : null}
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {subTasks.map(st => (
                        <li key={st._id} style={{ border: '1px solid #e9ecef', borderRadius: '6px', padding: '5px 10px', marginBottom: '6px', backgroundColor: st.status === 'Closed' ? '#f8f9fa' : '#fff' }}>
                          {editingSubTaskId === st._id && subTaskEditDraft ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                              <input type="text" value={subTaskEditDraft.name} onChange={(e) => setSubTaskEditDraft(d => ({ ...d, name: e.target.value }))} style={{ flex: '1 1 120px', minWidth: 100, padding: '4px 6px', fontSize: '13px' }} />
                              <input type="date" value={subTaskEditDraft.due_date} onChange={(e) => setSubTaskEditDraft(d => ({ ...d, due_date: e.target.value }))} style={{ padding: '4px 6px', fontSize: '13px' }} />
                              <select value={subTaskEditDraft.priority} onChange={(e) => setSubTaskEditDraft(d => ({ ...d, priority: e.target.value }))} style={{ padding: '4px 6px', fontSize: '13px' }}>
                                <option value="">—</option>
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                              </select>
                              <button
                                type="button"
                                onClick={() => {
                                  const subDue = subTaskEditDraft.due_date || '';
                                  const subDateCheck = validateDate(subDue);
                                  if (!subDateCheck.valid) { setBannerError(subDateCheck.message); return; }
                                  updateSubTask(st._id, { ...subTaskEditDraft, due_date: getDueDateForApi(subDue, '') });
                                }}
                                className="btn-link"
                                style={iconBtnStyle}
                                title="Save"
                              >
                                <IconSave size={18} />
                              </button>
                              <button
                                type="button"
                                onClick={() => { setEditingSubTaskId(null); setSubTaskEditDraft(null); }}
                                className="btn-link-muted"
                                style={iconBtnStyle}
                                title="Cancel"
                              >
                                <IconCancel size={18} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <div style={{ fontSize: '13px', minWidth: 0, flex: '1 1 auto', lineHeight: 1.35 }}>
                                  <span style={{ fontWeight: 500, wordBreak: 'break-word' }}>{st.name}</span>
                                  <span style={{ color: '#6c757d' }}>
                                    {' · '}{formatDueDate(st.due_date)} · {st.priority || '—'} · {st.status}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                                  <button type="button" onClick={() => startEditSubTask(st)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                                  <button type="button" onClick={() => deleteSubTask(st._id)} className="btn-link-muted" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                                  {st.status === 'Closed' ? (
                                    <button type="button" onClick={() => reopenSubTask(st._id)} className="btn-link" style={iconBtnStyle} title="Reopen"><IconReopen size={18} /></button>
                                  ) : (
                                    <button type="button" onClick={() => closeSubTask(st._id)} className="btn-link-muted" style={iconBtnStyle} title="Mark Complete"><IconClose size={18} /></button>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                    {subTasks.length === 0 && !showSubTaskForm && !showBulkSubTaskForm && (
                      <p style={{ fontSize: '13px', color: '#6c757d', margin: 0 }}>
                        No sub-tasks. Use Add or Bulk Add to create one.
                      </p>
                    )}
                  </>
                </>
              </div>
            </div>
          </div>
        );
      })()}

      {formExpanded && edit && (
        <div ref={editFormRef}>
        <table style={{ width: '100%', marginBottom: '10px' }}>
        <tr>
          <td style={{ verticalAlign: 'top', paddingRight: '12px', width: '40%', minWidth: '280px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(140px, 1fr)', gap: '8px 12px', alignItems: 'center' }}>
              <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#212529', textAlign: 'left', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>Name</label>
              <input type="text" value={name} placeholder="Name" required onChange={(e) => setName(e.target.value)} style={{ padding: '4px 8px', fontSize: '13px', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }} />
              <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#212529', textAlign: 'left', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>Due</label>
              <DateTimeFieldGroup
                layout="inline"
                date={due_date}
                onDateChange={setDueDate}
                time={due_time}
                onTimeChange={setDueTime}
                timeOptions={taskDueTimeOptions}
                required
                timeTitle="Time (optional)"
                dateStyle={{ padding: '4px 8px', fontSize: '13px', flex: '1 1 140px', minWidth: '120px', boxSizing: 'border-box' }}
                timeSelectStyle={{ padding: '4px 8px', fontSize: '13px', minWidth: '100px', boxSizing: 'border-box' }}
                style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}
              />
              <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#212529', textAlign: 'left', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>Amount($)</label>
              <input type="currency" value={amount ? amount : 0} placeholder="0" required onChange={(e) => setAmount(e.target.value)} style={{ padding: '4px 8px', fontSize: '13px', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }} />
              <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#212529', textAlign: 'left', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>Repeat</label>
              <RepeatPatternPicker anchorDate={due_date} value={repeatRule} onChange={setRepeatRule} dropdownPortal />
              <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#212529', textAlign: 'left', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} style={{ padding: '4px 8px', fontSize: '13px', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <option value="">—</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
              <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#212529', textAlign: 'left', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>Owner</label>
              <select value={owner} onChange={(e) => setOwner(e.target.value)} disabled={!canEditOwner} style={{ padding: '4px 8px', fontSize: '13px', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box', opacity: canEditOwner ? 1 : 0.8 }} title={!canEditOwner ? 'Only Admin and Super Admin can change owner' : undefined}>
                <option value="">Unassigned</option>
                {UserObjs.map((u) => (
                  <option key={u._id} value={u._id}>{getUserDisplayName(u) || u._id}</option>
                ))}
              </select>
              <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#212529', textAlign: 'left', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>Notes</label>
              <input type="text" value={notes} placeholder="Notes" onChange={(e) => setNotes(e.target.value)} style={{ padding: '4px 8px', fontSize: '13px', float: 'none', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginTop: '8px' }}>
              <button type="button" onClick={(e) => handleUpdateSubmit(e)} style={{ padding: '5px 12px', fontSize: '13px', marginRight: '8px' }}>
                Update
              </button>
            </div>
          </td>
          <td style={{ verticalAlign: 'top', paddingLeft: '12px', width: '60%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#212529', textAlign: 'left', float: 'none' }}>Project</label>
            </div>
            <select value={editProjectId} onChange={(e) => setEditProjectId(e.target.value)} style={{ width: '100%', padding: '4px 8px', fontSize: '13px', boxSizing: 'border-box' }}>
              <option value="">Select…</option>
              {(ProjectObjs || []).map((projectObj) => (
                <option key={String(projectObj._id)} value={String(projectObj._id)}>{projectObj.name}</option>
              ))}
            </select>
          </td>
        </tr>
      </table>
        </div>
      )}
      {linkContextFilters.length > 0 && (
        <div
          style={{
            marginBottom: 10,
            padding: '10px 14px',
            background: '#f6f8fa',
            border: '1px solid #d0d7de',
            borderRadius: 6,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '8px 12px',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: '#24292f' }}>Viewing tasks with:</span>
          {linkContextFilters.map((f, idx) => (
            <span
              key={`${f.id}-${idx}`}
              style={{
                fontSize: 13,
                padding: '4px 10px',
                background: '#ddf4ff',
                border: '1px solid rgba(84, 174, 255, 0.45)',
                borderRadius: 999,
                color: '#0550ae',
              }}
            >
              {f.text}
            </span>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={clearLinkContextFilters}
            style={{ marginLeft: 'auto' }}
          >
            Clear link filters
          </button>
        </div>
      )}
      {/* Compact filter bar — search + Filters slide-over */}
      <div className="toolbar toolbar--task-list toolbar--task-list-compact">
        <input
          type="text"
          id="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search name, notes, due, schedule, amount, repeat, status, project…"
          className="input input-search toolbar-control toolbar--task-list__search"
          aria-label="Search tasks"
        />
        <ViewModeIconToggle
          value={viewMode}
          onChange={setViewMode}
          modes={["calendar", "table", "card"]}
          ariaLabel="View"
        />
        <ListFiltersToolbarButton
          onClick={() => setTaskFiltersPanelOpen(true)}
          activeCount={taskListActiveFilterCount}
          activeSummary={taskListActiveFilterSummary}
        />
      </div>
      <TaskListFiltersSlideOver
        open={taskFiltersPanelOpen}
        onClose={() => setTaskFiltersPanelOpen(false)}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilterSynced}
        timeNav={timeNav}
        advancedFields={taskAdvancedFilterFields}
        advancedFilterApplied={advancedFilterApplied}
        onAdvancedFilterFieldChange={handleAdvancedFilterFieldChange}
        onClearPanelFilters={handleClearPanelFilters}
        pageViewPreferenceProps={taskPageViewPreferenceProps}
      />
      <EntityLabelFilterBar
        items={CurrentObjs}
        activeLabel={activeLabel}
        onActiveLabelChange={setActiveLabel}
        labelOptions={labelOptions}
        ariaLabel="Task labels"
        showUnlabeledFilter
      />
      {(viewMode === 'table' || viewMode === 'card') && (
        <ListPaginationBar {...paginationBarProps} />
      )}
      {(() => {
        const sortedObjs = sortedTaskObjs;

        return (
          <>
            {viewMode === 'table' && (
              <>
                <div className="table-wrapper table-wrapper-task-mgmt">
                <table className="task-management-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    {...getSelectAllCheckboxProps(handleSelectAll, selectedIds)}
                  />
                </th>
                {visibleTaskColumns.map((colId) => {
                  switch (colId) {
                    case 'seq':
                      return (
                        <th key={colId} className="sortable table-cell-numeric table-cell-task-seq" onClick={() => handleSort('seq')}>
                          Seq#
                        </th>
                      );
                    case 'name':
                      return (
                        <th key={colId} className="sortable table-cell-task-name" onClick={() => handleSort('name')}>
                          Name
                        </th>
                      );
                    case 'notes':
                      return (
                        <th key={colId} className="sortable table-cell-task-notes" onClick={() => handleSort('notes')}>
                          Notes
                        </th>
                      );
                    case 'due_date':
                      return (
                        <th key={colId} className="sortable table-cell-task-due" onClick={() => handleSort('due_date')}>
                          Due Date
                        </th>
                      );
                    case 'amount':
                      return (
                        <th key={colId} className="sortable table-cell-task-amount" onClick={() => handleSort('amount')}>
                          Amount($)
                        </th>
                      );
                    case 'repeat':
                      return (
                        <th key={colId} className="sortable table-cell-task-repeat" onClick={() => handleSort('repeat')}>
                          Repeat
                        </th>
                      );
                    case 'status':
                      return (
                        <th key={colId} className="sortable table-cell-task-status" onClick={() => handleSort('status')}>
                          Status
                        </th>
                      );
                    case 'schedule':
                      return (
                        <th key={colId} className="sortable table-cell-task-schedule" onClick={() => handleSort('schedule')}>
                          Schedule
                        </th>
                      );
                    case 'project':
                      return (
                        <th key={colId} className="sortable" onClick={() => handleSort('project')}>
                          Project
                        </th>
                      );
                    case 'category':
                      return (
                        <th key={colId} className="sortable" onClick={() => handleSort('category')}>
                          Category
                        </th>
                      );
                    case 'owner':
                      return (
                        <th key={colId} className="sortable" onClick={() => handleSort('owner')}>
                          Owner
                        </th>
                      );
                    case 'created_by':
                      return (
                        <th key={colId} className="sortable" onClick={() => handleSort('created_by')}>
                          Created By
                        </th>
                      );
                    case 'created_date':
                      return (
                        <th key={colId} className="sortable table-cell-task-created-col" onClick={() => handleSort('created_date')}>
                          Created
                        </th>
                      );
                    case 'completed_date':
                      return (
                        <th key={colId} className="sortable table-cell-task-completed-col" onClick={() => handleSort('completed_date')}>
                          Completed
                        </th>
                      );
                    case 'priority':
                      return (
                        <th key={colId} className="sortable table-cell-task-priority" onClick={() => handleSort('priority')}>
                          Priority
                        </th>
                      );
                    default:
                      return null;
                  }
                })}
                <TableActionColumnHeader
                  className="table-cell-task-action"
                  align="left"
                  onCustomizeColumns={() => setColumnModalOpen(true)}
                  customizeDisabled={columnPrefsLoading}
                />
              </tr>
            </thead>
            <tbody>
              {paginatedTaskObjs.map(currentObj => {
            return (
            <tr key={currentObj._id}>
              <td>
                  <input 
                    type="checkbox" 
                    checked={selectedIds.has(currentObj._id)}
                    onChange={(e) => handleSelectOne(currentObj._id, e.target.checked)}
                  />
              </td>
                <>
                  {visibleTaskColumns.map((colId) => {
                    switch (colId) {
                      case 'seq': {
                        const isEditing = seqEdit && String(seqEdit.taskId) === String(currentObj._id);
                        const isSaving = String(seqSavingId) === String(currentObj._id);
                        const displaySeq =
                          currentObj.seq != null && currentObj.seq !== ''
                            ? String(currentObj.seq)
                            : '—';
                        return (
                          <td key={colId} className="table-cell-numeric table-cell-task-seq">
                            {isEditing ? (
                              <input
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                disabled={isSaving}
                                value={seqEdit.draft}
                                onChange={(e) => setSeqEdit((prev) => (prev ? { ...prev, draft: e.target.value } : prev))}
                                onBlur={() => commitSeqEdit(currentObj)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelSeqEdit();
                                    return;
                                  }
                                  fieldCommitOnEnterKeyDown(e);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="input input-inline-edit input-number-no-spin"
                                aria-label="Seq#"
                              />
                            ) : (
                              <button
                                type="button"
                                className="btn-link table-cell-task-seq-btn"
                                onClick={(e) => startSeqEdit(currentObj, e)}
                                disabled={isSaving}
                                title="Click to edit Seq#"
                              >
                                {displaySeq}
                              </button>
                            )}
                          </td>
                        );
                      }
                      case 'name':
                        return (
                          <td key={colId} className="table-cell-task-name">
                            <button
                              type="button"
                              onClick={() => openTaskDetail(currentObj)}
                              className={`btn-link block${currentObj.status === 'Closed' ? ' item-text--completed' : ''}`}
                              title={(currentObj.name && String(currentObj.name).trim()) || undefined}
                            >
                              {currentObj.name}
                            </button>
                          </td>
                        );
                      case 'notes': {
                        const noteStr = (currentObj.notes != null && String(currentObj.notes).trim()) ? String(currentObj.notes).trim() : '';
                        return (
                          <td key={colId} className="table-cell-task-notes" title={noteStr || undefined}>
                            <span className="table-cell-task-notes-clip">{noteStr || '—'}</span>
                          </td>
                        );
                      }
                      case 'due_date': {
                        const dueFmt = formatDueDateTimeForTable(currentObj.due_date);
                        return (
                          <td key={colId} className="table-cell-task-due" title={dueFmt || undefined}>
                            {dueFmt}
                          </td>
                        );
                      }
                      case 'amount':
                        return (
                          <td key={colId} className="table-cell-task-amount">{currentObj.amount}</td>
                        );
                      case 'repeat':
                        return (
                          <td key={colId} className="table-cell-task-repeat" title={getRepeatLabel(currentObj.repeat_rule)}>
                            {getRepeatLabel(currentObj.repeat_rule)}
                          </td>
                        );
                      case 'status':
                        return (
                          <td key={colId} className="table-cell-task-status" title={currentObj.status || ''}>
                            {currentObj.status}
                          </td>
                        );
                      case 'schedule': {
                        const schedLabel = getTaskScheduleStatusLabel(currentObj);
                        const more = Math.max(0, (Number(currentObj.schedule_block_count) || 0) - 1);
                        const line = formatScheduledSlot(currentObj.schedule_start_time, currentObj.schedule_end_time, more, { dateOnly: true });
                        const lineFull = formatScheduledSlot(currentObj.schedule_start_time, currentObj.schedule_end_time, more, { includeEndTime: false });
                        const lineTrim = line != null && String(line).trim() ? String(line).trim() : '';
                        const titleTrim = lineFull != null && String(lineFull).trim() ? String(lineFull).trim() : lineTrim;
                        const badgeStyle = getScheduleStatusBadgeStyle(schedLabel);
                        return (
                          <td key={colId} className="table-cell-task-schedule">
                            <div
                              className="task-table-schedule-stack"
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: lineTrim ? '4px' : '0',
                                maxWidth: '100%',
                              }}
                            >
                              {lineTrim ? (
                                <span
                                  className="task-table-schedule-time"
                                  style={{
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={titleTrim}
                                >
                                  {lineTrim}
                                </span>
                              ) : null}
                              <span
                                className="task-table-schedule-badge"
                                title={schedLabel}
                                style={{
                                  ...badgeStyle,
                                  display: 'inline-block',
                                  flexShrink: 0,
                                  fontWeight: 600,
                                  padding: '3px 10px',
                                  borderRadius: '999px',
                                  lineHeight: 1.35,
                                  letterSpacing: '0.02em',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {schedLabel}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      case 'project': {
                        const projectLabel =
                          currentObj.project != null && String(currentObj.project).trim()
                            ? String(currentObj.project).trim()
                            : '—';
                        const rowProjectId =
                          currentObj.project_id != null && String(currentObj.project_id).trim()
                            ? String(currentObj.project_id).trim()
                            : projectIdForUnambiguousProjectName(currentObj.project) || '';
                        return (
                          <td key={colId} title={projectLabel !== '—' ? projectLabel : undefined}>
                            {rowProjectId ? (
                              <button
                                type="button"
                                className="btn-link block"
                                onClick={() => applyProjectIdUrlFilter(rowProjectId)}
                                title={projectLabel !== '—' ? `View tasks for ${projectLabel}` : 'View tasks for this project'}
                              >
                                {projectLabel}
                              </button>
                            ) : (
                              projectLabel
                            )}
                          </td>
                        );
                      }
                      case 'category':
                        return (
                          <td key={colId}>{currentObj.category || 'General'}</td>
                        );
                      case 'owner': {
                        const userById = Object.fromEntries((UserObjs || []).map(u => [u._id, getUserDisplayName(u)]));
                        return (
                          <td key={colId}>{userById[currentObj.owner] || 'Unassigned'}</td>
                        );
                      }
                      case 'created_by': {
                        const userById = Object.fromEntries((UserObjs || []).map(u => [u._id, getUserDisplayName(u)]));
                        return (
                          <td key={colId}>{userById[currentObj.created_by] || '—'}</td>
                        );
                      }
                      case 'created_date': {
                        const createdFmt = formatTimestampDateForTable(currentObj.created_date);
                        const createdFull = formatTimestampForTable(currentObj.created_date);
                        return (
                          <td key={colId} className="table-cell-task-created-col" title={createdFull !== '—' ? createdFull : undefined}>
                            {createdFmt}
                          </td>
                        );
                      }
                      case 'completed_date': {
                        const completedFmt = formatTimestampDateForTable(currentObj.completed_date);
                        const completedFull = formatTimestampForTable(currentObj.completed_date);
                        return (
                          <td key={colId} className="table-cell-task-completed-col" title={completedFull !== '—' ? completedFull : undefined}>
                            {completedFmt}
                          </td>
                        );
                      }
                      case 'priority':
                        return (
                          <td key={colId} className="table-cell-task-priority">{currentObj.priority || '—'}</td>
                        );
                      default:
                        return null;
                    }
                  })}
                  <td className="table-cell-task-action">
                    <RowActionIcons>
                      <EntityLabelCell
                        item={currentObj}
                        iconOnly
                        labelOptions={labelOptions}
                        saving={labelUpdating}
                        ariaLabel="Task labels"
                        onApplyLabels={(labels) => handleApplyLabelsForTask(currentObj, labels)}
                        onAddLabel={handleAddLabel}
                        onRenameLabel={handleRenameLabel}
                        onRemoveLabel={handleRemoveLabel}
                        setConfirmDialog={setConfirmDialog}
                      />
                      <button type="button" onClick={() => openTaskDetail(currentObj)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                      {currentObj.status === 'Closed' ? (
                        <button
                          type="button"
                          onClick={() => reopenOneTask(currentObj._id)}
                          className="btn-link"
                          style={iconBtnStyle}
                          title="Reopen"
                          disabled={taskStatusActionIds.has(String(currentObj._id))}
                          aria-disabled={taskStatusActionIds.has(String(currentObj._id)) ? 'true' : 'false'}
                        >
                          <IconReopen size={18} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => closeOneTask(currentObj)}
                          className="btn-link-muted"
                          style={iconBtnStyle}
                          title="Mark Complete"
                          disabled={taskStatusActionIds.has(String(currentObj._id))}
                          aria-disabled={taskStatusActionIds.has(String(currentObj._id)) ? 'true' : 'false'}
                        >
                          <IconClose size={18} />
                        </button>
                      )}
                      {currentObj.status !== 'Closed' && !taskHasTimeBlocks(currentObj) && hasPermission && hasPermission('menu.time_blocking') && (
                        <button
                          type="button"
                          onClick={() => { void openAutoScheduleFromTaskRow(currentObj); }}
                          className="btn-link"
                          style={iconBtnStyle}
                          title="Auto schedule"
                        >
                          <IconCalendar size={18} />
                        </button>
                      )}
                      <button type="button" onClick={() => duplicateDocument(currentObj)} className="btn-link" style={iconBtnStyle} title="Duplicate task"><IconDuplicate size={18} /></button>
                      <button type="button" onClick={() => confirmDeleteOne(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete task"><IconDelete size={18} /></button>
                    </RowActionIcons>
                  </td>
                </>
            </tr>
          );
          })}
        </tbody>
      </table>
                </div>
              </>
            )}
            {viewMode === 'card' && (
              <>
                <div className="cards-wrapper">
                  {paginatedTaskObjs.map((obj) => {
                    const userById = Object.fromEntries((UserObjs || []).map(u => [u._id, getUserDisplayName(u)]));
                    const ownerDisplay = userById[obj.owner] || 'Unassigned';
                    const statusClosed = obj.status === 'Closed';
                    const priorityClass = statusClosed ? 'card--closed' : (obj.priority === 'High' ? 'card--priority-high' : obj.priority === 'Low' ? 'card--priority-low' : 'card--priority-medium');
                    return (
                      <div
                        key={obj._id}
                        className={`card ${priorityClass}`}
                      >
                        <div className="card-header">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(obj._id)}
                            onChange={(e) => handleSelectOne(obj._id, e.target.checked)}
                          />
                          <div className="card-header-content">
                          <button
                            type="button"
                            onClick={() => openTaskDetail(obj)}
                            className={`btn-link card-title${statusClosed ? ' item-text--completed' : ''}`}
                          >
                              {obj.name}
                            </button>
                            <div className="card-date">
                              {formatDueDateTimeForTable(obj.due_date) || '—'}
                            </div>
                            {(() => {
                              const schedLabel = getTaskScheduleStatusLabel(obj);
                              const moreC = Math.max(0, (Number(obj.schedule_block_count) || 0) - 1);
                              const slot = formatScheduledSlot(obj.schedule_start_time, obj.schedule_end_time, moreC, { dateOnly: true });
                              const slotFull = formatScheduledSlot(obj.schedule_start_time, obj.schedule_end_time, moreC, { includeEndTime: false });
                              const badgeStyle = getScheduleStatusBadgeStyle(schedLabel);
                              return (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginTop: '6px',
                                    fontSize: '12px',
                                  }}
                                  title="Schedule: earliest block and status"
                                >
                                  {slot ? <span style={{ opacity: 0.95, color: '#495057' }} title={slotFull || undefined}>{slot}</span> : null}
                                  <span
                                    style={{
                                      ...badgeStyle,
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      padding: '2px 8px',
                                      borderRadius: '999px',
                                      lineHeight: 1.35,
                                      letterSpacing: '0.02em',
                                    }}
                                  >
                                    {schedLabel}
                                  </span>
                                </div>
                              );
                            })()}
                            {String(obj.notes || '').trim() ? (
                              <div className="card-notes card-meta-muted card-task-notes-preview" title={String(obj.notes).trim()}>
                                {String(obj.notes).trim()}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="card-meta-row">
                          <span title="Amount($)">${obj.amount ?? 0}</span>
                          <span>·</span>
                          <span>{getRepeatLabel(obj.repeat_rule)}</span>
                          <span>·</span>
                          <span className={statusClosed ? 'card-meta-emphasis' : ''}>{obj.status}</span>
                        </div>
                        <div className="card-meta-muted">
                          {ownerDisplay} {obj.priority ? `· ${obj.priority}` : ''}
                        </div>
                        <div className="card-meta-muted">
                          Created by {(() => {
                            const userById = Object.fromEntries((UserObjs || []).map(u => [u._id, getUserDisplayName(u)]));
                            return userById[obj.created_by] || '—';
                          })()}
                        </div>
                        {(() => {
                          const projectLabel =
                            obj.project != null && String(obj.project).trim()
                              ? String(obj.project).trim()
                              : '—';
                          const rowProjectId =
                            obj.project_id != null && String(obj.project_id).trim()
                              ? String(obj.project_id).trim()
                              : projectIdForUnambiguousProjectName(obj.project) || '';
                          if (!rowProjectId && projectLabel === '—') return null;
                          const nameFromList = rowProjectId
                            ? (ProjectObjs || []).find((p) => String(p._id) === rowProjectId)?.name
                            : '';
                          const linkText =
                            projectLabel !== '—'
                              ? projectLabel
                              : (nameFromList || 'View project');
                          const titleProject = projectLabel !== '—' ? projectLabel : (nameFromList || '');
                          return (
                            <div className="card-meta-muted">
                              <span style={{ marginRight: 4 }}>Project:</span>
                              {rowProjectId ? (
                                <button
                                  type="button"
                                  className="btn-link"
                                  style={{
                                    padding: 0,
                                    fontSize: 'inherit',
                                    fontWeight: 500,
                                    verticalAlign: 'baseline',
                                    textAlign: 'left',
                                  }}
                                  onClick={() => applyProjectIdUrlFilter(rowProjectId)}
                                  title={titleProject ? `View tasks for ${titleProject}` : 'View tasks for this project'}
                                >
                                  {linkText}
                                </button>
                              ) : (
                                <span>{projectLabel}</span>
                              )}
                            </div>
                          );
                        })()}
                        <div className="card-meta-muted" style={{ fontSize: '11px' }}>
                          <span title={formatTimestampForTable(obj.created_date)}>{formatTimestampDateForTable(obj.created_date)}</span>
                          {statusClosed ? (
                            <>
                              <span> · </span>
                              <span title={formatTimestampForTable(obj.completed_date)}>{formatTimestampDateForTable(obj.completed_date)}</span>
                            </>
                          ) : null}
                        </div>
                        <div className="card-footer">
                          <RowActionIcons variant="inline">
                          <EntityLabelCell
                            item={obj}
                            iconOnly
                            labelOptions={labelOptions}
                            saving={labelUpdating}
                            ariaLabel="Task labels"
                            onApplyLabels={(labels) => handleApplyLabelsForTask(obj, labels)}
                            onAddLabel={handleAddLabel}
                            onRenameLabel={handleRenameLabel}
                            onRemoveLabel={handleRemoveLabel}
                            setConfirmDialog={setConfirmDialog}
                          />
                          <button
                            type="button"
                            onClick={() => openTaskDetail(obj)}
                            className="btn-link"
                            style={iconBtnStyle}
                            title="Edit"
                          >
                            <IconEdit size={18} />
                          </button>
                          {statusClosed ? (
                            <button
                              type="button"
                              onClick={() => reopenOneTask(obj._id)}
                              className="btn-link"
                              style={iconBtnStyle}
                              title="Reopen"
                              disabled={taskStatusActionIds.has(String(obj._id))}
                              aria-disabled={taskStatusActionIds.has(String(obj._id)) ? 'true' : 'false'}
                            >
                              <IconReopen size={18} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => closeOneTask(obj)}
                              className="btn-link-muted"
                              style={iconBtnStyle}
                              title="Mark Complete"
                              disabled={taskStatusActionIds.has(String(obj._id))}
                              aria-disabled={taskStatusActionIds.has(String(obj._id)) ? 'true' : 'false'}
                            >
                              <IconClose size={18} />
                            </button>
                          )}
                          {!statusClosed && !taskHasTimeBlocks(obj) && hasPermission && hasPermission('menu.time_blocking') && (
                            <button type="button" onClick={() => { void openAutoScheduleFromTaskRow(obj); }} className="btn-link" style={iconBtnStyle} title="Auto schedule"><IconCalendar size={18} /></button>
                          )}
                          <button type="button" onClick={() => duplicateDocument(obj)} className="btn-link" style={iconBtnStyle} title="Duplicate task"><IconDuplicate size={18} /></button>
                          <button type="button" onClick={() => confirmDeleteOne(obj)} className="btn-link-danger" style={iconBtnStyle} title="Delete task"><IconDelete size={18} /></button>
                          </RowActionIcons>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {sortedObjs.length === 0 && (
                  <div className="empty-message">No tasks match your filters.</div>
                )}
              </>
            )}
            {viewMode === 'calendar' && (
              <ScheduleCalendar
                items={sortedObjs}
                view={timeNav.view}
                focusDateYmd={timeNav.focusDateYmd}
                onItemClick={openTaskDetail}
                emptyDayMessage="No tasks due this day"
                getDateValue={(obj) => obj?.due_date}
                getLabel={(obj) =>
                  getDueTimePart(obj.due_date)
                    ? `${obj.name} · ${dayjs(obj.due_date).format('h:mm A')}`
                    : obj.name
                }
                getSortValue={(obj) => {
                  const d = getDueInstantForComparison(obj.due_date);
                  return d && d.isValid() ? d.valueOf() : 0;
                }}
                getChipClassName={(obj) =>
                  `btn-link calendar${obj.status === 'Closed' ? ' item-text--completed' : ''}`
                }
                getItemBackgroundColor={(obj) =>
                  obj.status === 'Closed' ? '#e9ecef' : '#e7f3ff'
                }
                moreButtonClassName="btn-link calendar"
              />
            )}
          </>
        );
      })()}
    </div>
  );
}
export default Task